import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  PDFDocument,
  StandardFonts,
  rgb,
  PDFName,
  PDFCheckBox,
} from 'pdf-lib';
import { zipSync } from 'fflate';
import { buildCreditorsTextBlob } from './creditorTextGenerator';
import {
  normalizeStatementOfIntentTreatment,
  statementOfIntentSecuredDebts,
  statementOfIntentCollateralDescription,
  totalLinkedCollateralValue,
} from '../../utils/collateralDescription';
import {
  amendmentServiceDescription,
  amendmentServiceRecipients,
  appendAmendmentServiceList,
  caseFilingNoticeServiceLabel,
  mailingMatrixService,
} from '../../utils/creditorMailingMatrix';
import MailingMatrixServiceSelector from '../components/MailingMatrixServiceSelector';
  const API_URL = process.env.REACT_APP_API_URL;
  const API_URL2 = process.env.REACT_APP_BACKEND_URL;
const fmtDate = (s) => {
  if (!s) return '';
  const [year, month, day] = s.split('-');
  return `${month}/${day}/${year}`;
};
// helper to format dates as a long string, e.g. "Thursday, May 29, 2025"
const fmtLongDate = (s) => {
  if (!s) return '';
  const [year, month, day] = s.split('-').map(Number);
  // Construct date in local timezone to avoid UTC offset issues
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};
// Helper to format dates as MM/DD/YYYY
function formatDate(dateStr) {
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
}
// Safely decide whether to set a date field (skip invalid and 12/31/1969)
const shouldSetDate = (s) => {
  if (!s) return false;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return false;
  return !(d.getFullYear() === 1969 && d.getMonth() === 11 && d.getDate() === 31);
};
// Local ISO YYYY-MM-DD for "today" (no timezone surprises)
const isoToday = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const wrapPdfText = (font, text, fontSize, maxWidth) => {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  const lines = [];
  let line = '';
  words.forEach(word => {
    const candidate = line ? `${line} ${word}` : word;
    if (line && font.widthOfTextAtSize(candidate, fontSize) > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  });
  if (line) lines.push(line);
  return lines;
};

async function generateCaseFilingNoticeServiceCopy(data) {
  const notice = data?.caseFilingNotice;
  const filingDebtors = data?.amendedScheduleService?.filing_debtors || [];
  if (!notice?.pdf_url) {
    throw new Error('The selected Notice of Bankruptcy Case does not have a saved PDF.');
  }
  const missingSsn = filingDebtors.filter(debtor => !String(debtor?.ssn || '').trim());
  if (filingDebtors.length === 0 || missingSsn.length > 0) {
    throw new Error('Every filing debtor must have a full Social Security number before creating the mailed service copy.');
  }

  const currentToken = localStorage.getItem('accessToken');
  const response = await fetch(`${API_URL}${notice.pdf_url}`, {
    headers: currentToken ? { Authorization: `Bearer ${currentToken}` } : {},
  });
  if (!response.ok) {
    throw new Error('The saved Notice of Bankruptcy Case PDF could not be loaded.');
  }

  const source = await PDFDocument.load(await response.arrayBuffer(), { ignoreEncryption: true });
  const serviceCopy = await PDFDocument.create();
  const page = serviceCopy.addPage([612, 792]);
  const regular = await serviceCopy.embedFont(StandardFonts.Helvetica);
  const bold = await serviceCopy.embedFont(StandardFonts.HelveticaBold);
  const margin = 54;
  const maxWidth = page.getWidth() - (margin * 2);
  let y = 720;

  page.drawText('SERVICE COPY - CONTAINS FULL SOCIAL SECURITY NUMBERS', {
    x: margin,
    y,
    size: 13,
    font: bold,
    color: rgb(0.75, 0, 0),
  });
  y -= 24;
  page.drawText('DO NOT FILE THIS COPY WITH THE COURT', {
    x: margin,
    y,
    size: 12,
    font: bold,
  });
  y -= 42;
  page.drawText('Notice of Bankruptcy Case - Mailed Service Copy', {
    x: margin,
    y,
    size: 16,
    font: bold,
  });
  y -= 30;

  const details = [
    `Case number: ${data?.caseInfo?.case_number || ''}`,
    `Court docket citation: Document ${notice.docket_number}`,
  ];
  details.forEach(line => {
    page.drawText(line, { x: margin, y, size: 11, font: regular });
    y -= 19;
  });
  y -= 12;
  page.drawText('Filing debtor identification for mailed service:', {
    x: margin,
    y,
    size: 11,
    font: bold,
  });
  y -= 22;
  filingDebtors.forEach(debtor => {
    page.drawText(`${debtor.name}: ${debtor.ssn}`, {
      x: margin + 16,
      y,
      size: 11,
      font: regular,
    });
    y -= 20;
  });
  y -= 16;
  wrapPdfText(
    regular,
    'The court-filed amendment does not contain these full Social Security numbers. This cover sheet and the following Notice of Bankruptcy Case are for mailed service only.',
    10,
    maxWidth,
  ).forEach(line => {
    page.drawText(line, { x: margin, y, size: 10, font: regular });
    y -= 15;
  });

  const sourcePages = await serviceCopy.copyPages(source, source.getPageIndices());
  sourcePages.forEach(sourcePage => serviceCopy.addPage(sourcePage));
  const blob = new Blob(
    [await serviceCopy.save({ useObjectStreams: false })],
    { type: 'application/pdf' },
  );
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = `Case ${data?.caseInfo?.case_number || ''} Docket ${notice.docket_number} Service Copy.pdf`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  return blob;
}
// Import your PDF templates
const D_PAGE1 = '/forms/schedule-d-1.pdf';
const D_PAGE2 = '/forms/schedule-d-2.pdf';
const D_PAGE3 = '/forms/schedule-d-3.pdf';
const E_PAGE1 = '/forms/schedule-e-1.pdf';
const E_PAGE2 = '/forms/schedule-e-2.pdf';
const F_PAGE1 = '/forms/schedule-f-1.pdf';
const F_PAGE2 = '/forms/schedule-f-2.pdf';
const EF_LABELS = '/forms/schedule-ef-1.pdf';
const EF_TOTALS = '/forms/schedule-ef-2.pdf';
const G_PAGE1 = '/forms/schedule-g-1.pdf';
const G_PAGE2 = '/forms/schedule-g-2.pdf';
const H_PAGE1 = '/forms/schedule-h-1.pdf';
const H_PAGE2 = '/forms/schedule-h-2.pdf';
const S_PAGE1 = '/forms/soi-1.pdf'
const S_PAGE2 = '/forms/soi-2.pdf'
const S_PAGE3 = '/forms/soi-3.pdf'
const DEC_PAGE = '/forms/dec.pdf'
const MATRIX_FORM = '/forms/matrix-form.pdf'
const token = localStorage.getItem('accessToken');
async function generateEnvelopesPdf(creditors) {
    if (!creditors || creditors.length === 0) return;
    const envDoc = await PDFDocument.create();
    const seen = new Set();
    creditors = creditors.filter(c => {
      const key = [c.name, c.street1, c.street2 || '', c.city, c.state, c.zip_code]
        .join('|');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    // 1. Load & embed your logo (adjust the path as needed)
    const logoUrl = '/images/logo.png';
    const logoBytes = await fetch(logoUrl).then(r => r.arrayBuffer());
    const logoImage = await envDoc.embedPng(logoBytes);
    const logoDims = logoImage.scale(0.1);
  
    // 2. Prepare font and layout constants
    const font = await envDoc.embedFont(StandardFonts.Helvetica);
    const fontSize = 10;
    const lineHeight = fontSize + 4;
    const envelopeSize = [9.5 * 72, 4.125 * 72]; // width × height in points
    const margin = 25;
  
    // Your return address lines
    const returnAddress = [
      'Cisowski Law',
      '3119 Bristol Hwy Ste 217',
      'Johnson City, TN 37601',
    ];
  
    // 3. One page per creditor
    for (let creditorIndex = 0; creditorIndex < creditors.length; creditorIndex += 1) {
      const c = creditors[creditorIndex];
      const page = envDoc.addPage(envelopeSize);
      const { width, height } = page.getSize();
  
      // 3a. Draw logo in top-left
      page.drawImage(logoImage, {
        x: margin,
        y: height - margin - logoDims.height,
        width: logoDims.width,
        height: logoDims.height,
      });
  
      // 3b. Draw return address to the right of the logo
      let yPos = height - margin - 9;
      for (const line of returnAddress) {
        page.drawText(line, {
          x: margin + logoDims.width + 10,
          y: yPos,
          size: fontSize,
          font,
        });
        yPos -= fontSize + 2;
      }

      if (String(c.method || '').toLowerCase() === 'certified') {
        const certifiedLabel = 'CERTIFIED MAIL';
        page.drawText(certifiedLabel, {
          x: width - margin - font.widthOfTextAtSize(certifiedLabel, fontSize),
          y: height - margin - fontSize,
          size: fontSize,
          font,
        });
      }
  
      // draw recipient address centered vertically and horizontally
      const addrLines = [
        c.name,
        `${c.street1}${c.street2?.trim() ? `, ${c.street2}` : ''}`,
        `${c.city}, ${c.state} ${c.zip_code}`,
      ];
      // measure max line width
      const lineWidths = addrLines.map(line => font.widthOfTextAtSize(line, fontSize));
      const maxWidth = Math.max(...lineWidths);
      const startX = (width - maxWidth) / 2;
      let startY = height / 2 + (addrLines.length / 2) * lineHeight;
      for (const line of addrLines) {
        page.drawText(line, {
          x: startX,
          y: startY,
          size: fontSize,
          font,
        });
        startY -= lineHeight;
      }
      // Give the browser a chance to paint while creating a large matrix packet.
      if ((creditorIndex + 1) % 25 === 0) {
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    }
  
    // 4. Save & trigger download
    const pdfBytes = await envDoc.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'envelopes.pdf';
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
  }

async function generateEnvelopesPdf2(creditors, options = {}) {
    if (!creditors || creditors.length === 0) return;
    const envDoc = await PDFDocument.create();
    const seen = new Set();
    creditors = creditors.filter(c => {
      const key = [c.account_name, c.street1, c.street2 || '', c.city, c.state, c.zip_code]
        .join('|');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    // 1. Load & embed your logo (adjust the path as needed)
    const logoUrl = '/images/logo.png';
    const logoBytes = await fetch(logoUrl).then(r => r.arrayBuffer());
    const logoImage = await envDoc.embedPng(logoBytes);
    const logoDims = logoImage.scale(0.1);
  
    // 2. Prepare font and layout constants
    const font = await envDoc.embedFont(StandardFonts.Helvetica);
    const fontSize = 10;
    const lineHeight = fontSize + 4;
    const envelopeSize = [9.5 * 72, 4.125 * 72]; // width × height in points
    const margin = 25;
  
    // Your return address lines
    const returnAddress = [
      'Cisowski Law',
      '3119 Bristol Hwy Ste 217',
      'Johnson City, TN 37601',
    ];
  
    // 3. One page per creditor
    for (const c of creditors) {
      const page = envDoc.addPage(envelopeSize);
      const { width, height } = page.getSize();
  
      // 3a. Draw logo in top-left
      page.drawImage(logoImage, {
        x: margin,
        y: height - margin - logoDims.height,
        width: logoDims.width,
        height: logoDims.height,
      });
  
      // 3b. Draw return address to the right of the logo
      let yPos = height - margin - 9;
      for (const line of returnAddress) {
        page.drawText(line, {
          x: margin + logoDims.width + 10,
          y: yPos,
          size: fontSize,
          font,
        });
        yPos -= fontSize + 2;
      }
  
      // draw recipient address centered vertically and horizontally
      const addrLines = [
        c.account_name,
        `${c.street1}${c.street2?.trim() ? `, ${c.street2}` : ''}`,
        `${c.city}, ${c.state} ${c.zip_code}`,
      ];
      // measure max line width
      const lineWidths = addrLines.map(line => font.widthOfTextAtSize(line, fontSize));
      const maxWidth = Math.max(...lineWidths);
      const startX = (width - maxWidth) / 2;
      let startY = height / 2 + (addrLines.length / 2) * lineHeight;
      for (const line of addrLines) {
        page.drawText(line, {
          x: startX,
          y: startY,
          size: fontSize,
          font,
        });
        startY -= lineHeight;
      }
    }
  
    // 4. Save & trigger download
    const pdfBytes = await envDoc.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    if (options.download !== false) {
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = 'envelopes.pdf';
      link.click();
      URL.revokeObjectURL(objectUrl);
    }
    return blob;
  }
// Create a letter‑sized PDF listing served creditors
async function generateCreditorsListPdf(creditors) {
    // Create the document
    const listDoc = await PDFDocument.create();
    creditors = Array.isArray(creditors) ? creditors : [];
  
    // Deduplicate by name & address
    const seen = new Set();
    creditors = creditors.filter(c => {
      const key = [c.name, c.street1, c.street2 || '', c.city, c.state, c.zip_code]
        .join('|');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  
    const helvetica = await listDoc.embedFont(StandardFonts.Helvetica);
    const pageSize = [612, 792]; // Letter size
    let page = listDoc.addPage(pageSize);
    const { width, height } = page.getSize();
    const margin = 50;
    let y = height - margin;
    const lineHeight = 12;
    const indent = margin + 10;

    if (creditors.length === 0) {
      const message = 'No additional creditors were served.';
      const fontSize = 11;
      const textWidth = helvetica.widthOfTextAtSize(message, fontSize);
      page.drawText(message, {
        x: (width - textWidth) / 2,
        y: height - 100,
        size: fontSize,
        font: helvetica,
      });
      return listDoc;
    }
  
    for (const c of creditors) {
      // Name
      page.drawText(c.name, { x: margin, y, size: 10, font: helvetica });
      y -= lineHeight;
      // Address line 1
      page.drawText(
        `${c.street1}${c.street2?.trim() ? ', ' + c.street2 : ''}`,
        { x: indent, y, size: 10, font: helvetica }
      );
      y -= lineHeight;
      // City, State Zip
      page.drawText(
        `${c.city}, ${c.state} ${c.zip_code}`,
        { x: indent, y, size: 10, font: helvetica }
      );
      y -= lineHeight;
      // Method
      page.drawText(`Served via: ${c.method}`, { x: indent, y, size: 10, font: helvetica });
      y -= lineHeight * 1.5;
  
      if (y < margin) {
        page = listDoc.addPage(pageSize);
        y = height - margin;
      }
    }
  
    return listDoc;
  }

  async function generateCreditorsListPdf2(creditors) {
    // Create the document
    const listDoc = await PDFDocument.create();
  
    // Deduplicate by name & address
    const seen = new Set();
    creditors = creditors.filter(c => {
      const key = [c.name, c.street1, c.street2 || '', c.city, c.state, c.zip_code]
        .join('|');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  
    const helvetica = await listDoc.embedFont(StandardFonts.Helvetica);
    const pageSize = [612, 792]; // Letter size
    let page = listDoc.addPage(pageSize);
    const { width, height } = page.getSize();
    const margin = 50;
    let y = height - margin;
    const lineHeight = 12;
    const indent = margin + 10;
  
    for (const c of creditors) {
      // Name
      page.drawText(c.account_name, { x: margin, y, size: 10, font: helvetica });
      y -= lineHeight;
      // Address line 1
      page.drawText(
        `${c.street1}${c.street2?.trim() ? ', ' + c.street2 : ''}`,
        { x: indent, y, size: 10, font: helvetica }
      );
      y -= lineHeight;
      // City, State Zip
      page.drawText(
        `${c.city}, ${c.state} ${c.zip_code}`,
        { x: indent, y, size: 10, font: helvetica }
      );
      y -= lineHeight;
      // Method

  
      if (y < margin) {
        page = listDoc.addPage(pageSize);
        y = height - margin;
      }

      if (c.codebtor && c.codebtor.trim()) {
        page.drawText(c.codebtor, { x: margin, y, size: 10, font: helvetica });
        y -= lineHeight;
        page.drawText(c.codebtor_street1, { x: indent, y, size: 10, font: helvetica });
        y -= lineHeight;
        if (c.codebtor_street2 && c.codebtor_street2.trim() !== "") {
          page.drawText(c.codebtor_street2, { x: indent, y, size: 10, font: helvetica });
          y -= lineHeight;
        }
        page.drawText(
          `${c.codebtor_city}, ${c.codebtor_state} ${c.codebtor_zip_code}`,
          { x: indent, y, size: 10, font: helvetica }
        );
        y -= lineHeight;

        if (y < margin) {
          page = listDoc.addPage(pageSize);
          y = height - margin;
        }
      
      }

            if (y < margin) {
        page = listDoc.addPage(pageSize);
        y = height - margin;
      }
    }
  
    return listDoc;
  }
  export async function generateSchedulesPdfLocal(data, amended, selectedSchedules, creditors, options = {}) {
    // Destructure caseInfo and default arrays

    //const type = scheduleType;
    const isAmended = amended;
    const caseInfo = data.caseInfo || {};
    const caseDebtors = Array.isArray(caseInfo.debtors) ? caseInfo.debtors : [];
    const requiresCaseFilingNotice = Boolean(data.amended) && (
      selectedSchedules.includes('all')
      || selectedSchedules.includes('D')
      || selectedSchedules.includes('EF')
    );
    if (requiresCaseFilingNotice && !data.caseFilingNotice?.docket_number) {
      throw new Error('Select the Notice of Bankruptcy Case docket before generating an amended Schedule D or E/F.');
    }
    // Sum every linked collateral item. Creditor asset links include the value
    // field used by each concrete asset model (value_total, value, or amount).
    // caseInfo remains a fallback for older vehicle/real-property responses.
    function getAssetValue(debt) {
      const total = totalLinkedCollateralValue(debt, caseInfo);
      return total === null ? '' : total.toFixed(2);
    }

    // Helper to get property description from linked asset
    function getPropertyDescription(debt) {
      return statementOfIntentCollateralDescription(debt, caseInfo);
    }

    // Helper to format dates as MM/DD/YYYY
    function formatDate(dateStr) {
      const d = new Date(dateStr);
      return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
    }
    console.log(selectedSchedules)
    if ((selectedSchedules.includes('SOI') || selectedSchedules.includes('all')) && data.caseInfo.chapter === '7'){
      let sharedPageCount = 1;
      const mergedIntent = await PDFDocument.create();
      const primaryDebtor = caseDebtors.find(d => d.is_primary && d.is_filing);
      const secondaryDebtor = caseDebtors.find(d => !d.is_primary && d.is_filing);
      const s1Bytes = await fetch(S_PAGE1).then(r => r.arrayBuffer());
      const s1Doc = await PDFDocument.load(s1Bytes);
      const page1 = s1Doc.getPage(0);
      const formS1 = s1Doc.getForm();

      // Use the complete creditor list when available. Some imported or legacy
      // non-auto/non-mortgage debts were excluded earlier by the caller's exact
      // lowercase `secured` filter before this generator ever saw them.
      const securedDebts = statementOfIntentSecuredDebts(
        data.allDebts?.length ? data.allDebts : data.debtsD,
      );
      const totalSPages = 2 + Math.ceil(Math.max(securedDebts.length - 4, 0) / 6);
      const leases = data.debtsG.filter(d => d.debt_type === 'executory' && !d.additional_notified);
      if (data.caseInfo.case_number)
      formS1.getTextField('case_number').setText(data.caseInfo.case_number);
      if (data.amended) {
        formS1.getCheckBox('amended').check();
      } else {
        formS1.getCheckBox('amended').uncheck();
      }
      formS1.getTextField('district').setText('Eastern District of Tennessee');

      if (primaryDebtor) {
        formS1.getTextField('Debtor 1').setText(
          [primaryDebtor.first_name, primaryDebtor.second_name, primaryDebtor.last_name]
            .filter(Boolean).join(' ')
        );
      }
      if (secondaryDebtor) {
        formS1.getTextField('Debtor 2').setText(
          [secondaryDebtor.first_name, secondaryDebtor.second_name, secondaryDebtor.last_name]
            .filter(Boolean).join(' ')
        );
      }
      securedDebts.slice(0, 4).forEach((c, idx) => {
      const suffix = idx + 1;
      const treatment = normalizeStatementOfIntentTreatment(
        c.statement_of_intent_secured_treatment,
      );
      formS1.getTextField(`account_name_${suffix}`).setText(c.account_name);
      // Only last 4 digits of account number
     
      formS1.getTextField(`property_description_${suffix}`).setText(getPropertyDescription(c));
      if (treatment === 'surrender'){
        formS1.getCheckBox(`surrender_${suffix}`).check();
        formS1.getCheckBox(`exempt_no_${suffix}`).check();
      }
      else if (treatment === 'redeem'){
        formS1.getCheckBox(`redeem_${suffix}`).check();
        formS1.getCheckBox(`exempt_yes_${suffix}`).check();
      }
      else if (treatment === 'reaffirm'){
        formS1.getCheckBox(`reaffirm_${suffix}`).check();
        formS1.getCheckBox(`exempt_yes_${suffix}`).check();
      }
      else if (treatment === 'other'){
        formS1.getCheckBox(`other_${suffix}`).check();
        formS1.getTextField(`explain_${suffix}`).setText(c.statement_of_intent_other_description);
        formS1.getCheckBox(`exempt_yes_${suffix}`).check();
      }
      
      });
      sharedPageCount++;
      const width1 = page1.getWidth();
      const height1 = page1.getHeight();
      formS1.getFields().forEach(field => {
        if (field instanceof PDFCheckBox && !field.isChecked()) {
          field.acroField.getWidgets().forEach(widget => {
            const dict = widget.acroAnnotation?.dict || widget.dict;
            // move widget off-page by setting its Rect to a small area beyond the page bounds
            dict.set(
              PDFName.of('Rect'),
              s1Doc.context.obj([
                width1 + 5,   // lower-left x just outside page
                height1 + 5,  // lower-left y just outside page
                width1 + 15,  // upper-right x a bit further out
                height1 + 15  // upper-right y a bit further out
              ])
            );
          });
        }
      });
      // Flatten form fields before copying page
      formS1.flatten();

      // Add the filled page to the merged PDF
      const [s1Page] = await mergedIntent.copyPages(s1Doc, [0]);
      mergedIntent.addPage(s1Page);

      const contD = securedDebts.slice(4);
    // running total starts with first two balances


    for (let i = 0; i < contD.length; i += 6) {
      const chunk = contD.slice(i, i + 6);
      const s2Bytes = await fetch(S_PAGE2).then(r => r.arrayBuffer());
      const s2Doc = await PDFDocument.load(s2Bytes);
      const formS2 = s2Doc.getForm();
      if (primaryDebtor) {
        formS2.getTextField('Debtor 1').setText(
          [primaryDebtor.first_name, primaryDebtor.second_name, primaryDebtor.last_name]
            .filter(Boolean).join(' ')
        );
      }
      if (data.caseInfo.case_number)
      formS2.getTextField('case_number').setText(data.caseInfo.case_number);
    
      chunk.forEach((c, idx) => {
        const n = idx + 1; // 1,2,3
        const suffix = `_${n}`;
        const treatment = normalizeStatementOfIntentTreatment(
          c.statement_of_intent_secured_treatment,
        );
        // Creditor numbering field (e.g., 2.3, 2.4, 2.5)


        formS2.getTextField(`account_name${suffix}`).setText(c.account_name);
        formS2.getTextField(`property_description${suffix}`).setText(getPropertyDescription(c));
        if (treatment === 'surrender'){
        formS2.getCheckBox(`surrender${suffix}`).check();
        formS2.getCheckBox(`exempt_no${suffix}`).check();
      }
      else if (treatment === 'redeem'){
        formS2.getCheckBox(`redeem${suffix}`).check();
        formS2.getCheckBox(`exempt_yes${suffix}`).check();
      }
      else if (treatment === 'reaffirm'){
        formS2.getCheckBox(`reaffirm${suffix}`).check();
        formS2.getCheckBox(`exempt_yes${suffix}`).check();
      }
      else if (treatment === 'other'){
        formS2.getCheckBox(`other${suffix}`).check();
        formS2.getTextField(`explain${suffix}`).setText(c.statement_of_intent_other_description);
        formS2.getCheckBox(`exempt_yes${suffix}`).check();
      }
      });
      
      formS2.getTextField(`page_count`).setText(sharedPageCount.toString());
      formS2.getTextField(`page_total`).setText(totalSPages.toString());
      sharedPageCount++;
      const width1 = page1.getWidth();
      const height1 = page1.getHeight();
      formS2.getFields().forEach(field => {
        if (field instanceof PDFCheckBox && !field.isChecked()) {
          field.acroField.getWidgets().forEach(widget => {
            const dict = widget.acroAnnotation?.dict || widget.dict;
            // move widget off-page by setting its Rect to a small area beyond the page bounds
            dict.set(
              PDFName.of('Rect'),
              s1Doc.context.obj([
                width1 + 5,   // lower-left x just outside page
                height1 + 5,  // lower-left y just outside page
                width1 + 15,  // upper-right x a bit further out
                height1 + 15  // upper-right y a bit further out
              ])
            );
          });
        }
      });
      formS2.flatten();

      // Add the filled page to the merged PDF
      const [s2Page] = await mergedIntent.copyPages(s2Doc, [0]);
      mergedIntent.addPage(s2Page);
      }

      const s3Bytes = await fetch(S_PAGE3).then(r => r.arrayBuffer());
      const s3Doc = await PDFDocument.load(s3Bytes);
      const page3 = s3Doc.getPage(0);
      const formS3 = s3Doc.getForm();
      // If amended, signature/exec dates should be today's date; else use execution_date
      const execDateISO = data.amended ? isoToday() : (data.caseInfo.execution_date || '');

      if (data.caseInfo.case_number)
      formS3.getTextField('case_number').setText(data.caseInfo.case_number);

      if (primaryDebtor) {
        formS3.getTextField('Debtor 1').setText(
          [primaryDebtor.first_name, primaryDebtor.second_name, primaryDebtor.last_name]
            .filter(Boolean).join(' ')
        );
        formS3.getTextField('Debtor 1_signature').setText(
          ['s/ ',primaryDebtor.first_name, primaryDebtor.second_name, primaryDebtor.last_name]
            .filter(Boolean).join(' ')
        );
        formS3.getTextField('execution_date_Debtor 1').setText(fmtDate(execDateISO));
      }
      if (secondaryDebtor) {
        formS3.getTextField('Debtor 2_signature').setText(
          ['s/ ',secondaryDebtor.first_name, secondaryDebtor.second_name, secondaryDebtor.last_name]
            .filter(Boolean).join(' ')
        );
        formS3.getTextField('execution_date_Debtor 2').setText(fmtDate(execDateISO));
      }

      leases.slice(0, 7).forEach((c, idx) => {
      const suffix = idx + 1;
      formS3.getTextField(`account_name_${suffix}`).setText(c.account_name);
      // Only last 4 digits of account number
      formS3.getTextField(`property_description_${suffix}`).setText(c.description_of_lease);
      if (c.statement_of_intent_assumption){
        formS3.getCheckBox(`assume_${suffix}`).check();
      }
      else{
        formS3.getCheckBox(`reject_${suffix}`).check();
      }
      
      });
      formS3.getTextField(`page_count`).setText(totalSPages.toString());
      formS3.getTextField(`page_total`).setText(totalSPages.toString());
      sharedPageCount++;
      formS3.getFields().forEach(field => {
        if (field instanceof PDFCheckBox && !field.isChecked()) {
          field.acroField.getWidgets().forEach(widget => {
            const dict = widget.acroAnnotation?.dict || widget.dict;
            // move widget off-page by setting its Rect to a small area beyond the page bounds
            dict.set(
              PDFName.of('Rect'),
              s3Doc.context.obj([
                width1 + 5,   // lower-left x just outside page
                height1 + 5,  // lower-left y just outside page
                width1 + 15,  // upper-right x a bit further out
                height1 + 15  // upper-right y a bit further out
              ])
            );
          });
        }
      });
      // Flatten form fields before copying page
      formS3.flatten();

      // Add the filled page to the merged PDF
      const [s3Page] = await mergedIntent.copyPages(s3Doc, [0]);
      mergedIntent.addPage(s3Page);


      
      let amendedPdfDoc = null;
      let serviceEnvelopeBlob = null;
      if (data.amended){

          const amendedUrl = '/forms/notice-of-amendment.pdf'
          const amendedExistingPdfBytes = await fetch(amendedUrl).then(res => res.arrayBuffer());
          amendedPdfDoc = await PDFDocument.load(amendedExistingPdfBytes);
          const amendedForm = amendedPdfDoc.getForm();
  
          (data.caseInfo.debtors || []).forEach((debtor, index) => {
              const amendPrefix = debtor.is_primary ? 'debtor1' : 'debtor2';
              amendedForm.getTextField(`${amendPrefix}`).setText(`${(debtor.first_name || '')} ${(debtor.second_name || '')} ${(debtor.last_name || '')} ${(debtor.suffix || '')}`);
          });
          amendedForm.getTextField('case_number').setText(data.caseInfo.case_number || '');
          amendedForm.getTextField('chapter').setText(data.caseInfo.chapter || '');
          amendedForm.getTextField('date').setText(fmtLongDate(isoToday()));
          amendedForm.getTextField('amended_files').setText("Amended Statement of Intent");
          amendedForm.getTextField('items_served').setText("Amended Statement of Intent and Notice of Amendment");
          amendedForm.getTextField('reason_amended').setText(data.amendedReason);
          amendedForm.getTextField('creditors_served_description').setText(
            amendmentServiceDescription(data),
          );
          const helvetica = await amendedPdfDoc.embedFont(StandardFonts.Helvetica);
          amendedForm.updateFieldAppearances(helvetica);
          amendedForm.flatten();
          
      }
      else {
          const amendedUrl = '/forms/cos-intent.pdf'
          const amendedExistingPdfBytes = await fetch(amendedUrl).then(res => res.arrayBuffer());
          amendedPdfDoc = await PDFDocument.load(amendedExistingPdfBytes);
          const amendedForm = amendedPdfDoc.getForm();
  
          (data.caseInfo.debtors || []).forEach((debtor, index) => {
              const amendPrefix = debtor.is_primary ? 'debtor1' : 'debtor2';
              amendedForm.getTextField(`${amendPrefix}`).setText(`${(debtor.first_name || '')} ${(debtor.second_name || '')} ${(debtor.last_name || '')} ${(debtor.suffix || '')}`);
          });
          if (data.caseInfo.case_number) amendedForm.getTextField('case_number').setText(data.caseInfo.case_number || '');
          amendedForm.getTextField('chapter').setText(data.caseInfo.chapter || '');
          amendedForm.getTextField('date').setText(fmtLongDate(isoToday()));
          const helvetica = await amendedPdfDoc.embedFont(StandardFonts.Helvetica);
          amendedForm.updateFieldAppearances(helvetica);
          amendedForm.flatten();
      }
          if (data.amended && amendedPdfDoc) {
        // then amended form
        //const decPages = await mergedIntent.copyPages(decPdfDoc, decPdfDoc.getPageIndices());
        //decPages.forEach(p => mergedIntent.addPage(p));

        const amendedPages = await mergedIntent.copyPages(amendedPdfDoc, amendedPdfDoc.getPageIndices());
        amendedPages.forEach(p => mergedIntent.addPage(p));
        // then creditor list
        await appendAmendmentServiceList(mergedIntent, data, generateCreditorsListPdf);
        const envelopes = await generateEnvelopesPdf(amendmentServiceRecipients(data));
      }
        else {
        // then amended form
        const amendedPages = await mergedIntent.copyPages(amendedPdfDoc, amendedPdfDoc.getPageIndices());
        amendedPages.forEach(p => mergedIntent.addPage(p));
        // then creditor list
        const listDoc   = await generateCreditorsListPdf2([...securedDebts, ...leases]);
        serviceEnvelopeBlob = await generateEnvelopesPdf2(
          [...securedDebts, ...leases],
          { download: !options.servicePacketOnly },
        );
        const listPages = await mergedIntent.copyPages(listDoc, listDoc.getPageIndices());
        listPages.forEach(p => mergedIntent.addPage(p));
      }
      const pdfBytes = await mergedIntent.save({
      useObjectStreams: false,
      compress: false,
    });
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const formData2 = new FormData();
    formData2.append('file', blob, 'draft.pdf');
    let statementOfIntentBlob = blob;
    try {
      const response = await fetch(`${API_URL2}/optimize-pdf/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData2,
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Optimization failed');
      statementOfIntentBlob = await response.blob();
    } catch (error) {
      console.error(error);
    }

    if (options.servicePacketOnly) {
      const packetFiles = {
        'Statement of Intent.pdf': new Uint8Array(await statementOfIntentBlob.arrayBuffer()),
      };
      if (serviceEnvelopeBlob) {
        packetFiles['envelopes/Envelopes.pdf'] = new Uint8Array(
          await serviceEnvelopeBlob.arrayBuffer(),
        );
      }
      return new Blob([zipSync(packetFiles)], { type: 'application/zip' });
    }

    const objectUrl = URL.createObjectURL(statementOfIntentBlob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = '108 Statement of Intent.pdf';
    link.click();
    URL.revokeObjectURL(objectUrl);
      }

    const merged = await PDFDocument.create();
    // === Shared pagination for Schedules E, F, and EF ===
    const prioShared = data.debtsE.filter(d => d.debt_type === 'priority' && !d.additional_notified);
    const totalEPages = 1 + Math.ceil(Math.max(prioShared.length - 2, 0) / 3);
    const nonprioShared = data.debtsF.filter(d => d.debt_type === 'unsecured' && !d.additional_notified);
    const totalFPages = 1 + Math.ceil(Math.max(nonprioShared.length - 3, 0) / 3);
    const notifyShared = [...data.debtsE, ...data.debtsF].filter(d => d.additional_notified);
    const totalEFNotifPages = Math.ceil(notifyShared.length / 7);
    const totalSharedPages = totalEPages + totalFPages + totalEFNotifPages + 1; // +1 for EF totals
    let sharedPageCount = 1;



    // Helper to load a PDF and copy pages
    async function addPdfPages(srcUrl, pageIndices = null) {
      const bytes = await fetch(srcUrl).then(r => r.arrayBuffer());
      const doc = await PDFDocument.load(bytes);
      const indices = pageIndices ?? doc.getPageIndices();
      const pages = await merged.copyPages(doc, indices);
      pages.forEach(p => merged.addPage(p));
    }
    const primaryDebtor = caseDebtors.find(d => d.is_primary && d.is_filing);
    const secondaryDebtor = caseDebtors.find(d => !d.is_primary && d.is_filing);
    if (selectedSchedules.includes('D') || selectedSchedules.includes('all')) {
    // 1. Schedule D – Page 1 (first two secured creditors)
    // Load the template and get the form
    const d1Bytes = await fetch(D_PAGE1).then(r => r.arrayBuffer());
    const d1Doc = await PDFDocument.load(d1Bytes);
    const page1 = d1Doc.getPage(0);
    const formD1 = d1Doc.getForm();

    // Calculate total number of D pages (1 main + continuation + notification)
    const securedDebts = data.debtsD.filter(d => d.debt_type === 'secured' && !d.additional_notified && !d.who_owes !== "authorized");
    const contCount = Math.ceil(Math.max(securedDebts.length - 2, 0) / 3);
    const notifySecured = data.debtsD.filter(d => d.debt_type === 'secured' && d.additional_notified);
    const notifyCount = Math.ceil(notifySecured.length / 6);
    const totalDPages = 1 + contCount + notifyCount;

    // Check secured_yes or secured_no
    if (securedDebts.length) {
      formD1.getCheckBox('secured_yes').check();
      // Move unsecured "no" box out of sight
      const noWidget = formD1.getCheckBox('secured_no').acroField.getWidgets()[0];
      noWidget.setRectangle({
        x: page1.getWidth() - 20,
        y: page1.getHeight() - 20,
        width: 10,
        height: 10,
      });
    } else {
      formD1.getCheckBox('secured_no').check();
      const yesWidget = formD1.getCheckBox('secured_yes').acroField.getWidgets()[0];
      yesWidget.setRectangle({
        x: page1.getWidth() - 20,
        y: page1.getHeight() - 20,
        width: 10,
        height: 10,
      });
    }

    // Debtor names: primary = debtor1, secondary = debtor2 (only if filing)

    if (primaryDebtor) {
      formD1.getTextField('debtor1').setText(
        [primaryDebtor.first_name, primaryDebtor.second_name, primaryDebtor.last_name]
          .filter(Boolean).join(' ')
      );
    }
    if (secondaryDebtor) {
      formD1.getTextField('debtor2').setText(
        [secondaryDebtor.first_name, secondaryDebtor.second_name, secondaryDebtor.last_name]
          .filter(Boolean).join(' ')
      );
    }

    // Case metadata
    if (data.caseInfo.case_number)
    formD1.getTextField('case_number').setText(data.caseInfo.case_number);
    if (data.amended) {
      formD1.getCheckBox('amended').check();
    } else {
      formD1.getCheckBox('amended').uncheck();
    }
    formD1.getTextField('district').setText('Eastern District of Tennessee');

    // Fill first two secured debts
    securedDebts.slice(0, 2).forEach((c, idx) => {
      const suffix = idx === 0 ? '' : '_2';
      formD1.getTextField(`account_name${suffix}`).setText(c.account_name);
      // Only last 4 digits of account number
      const acctNum1 = c.account_number || '';
      formD1.getTextField(`account_number${suffix}`).setText(acctNum1.slice(-4));
      formD1.getTextField(`property_description${suffix}`).setText(getPropertyDescription(c));
      formD1.getTextField(`street1${suffix}`).setText(c.street1);
      formD1.getTextField(`street2${suffix}`).setText(c.street2 || '');
      formD1.getTextField(`city${suffix}`).setText(c.city);
      formD1.getTextField(`state${suffix}`).setText(c.state);
      formD1.getTextField(`zip_code${suffix}`).setText(c.zip_code);
      if (shouldSetDate(c.date_opened))
        formD1.getTextField(`date_incurred${suffix}`).setText(formatDate(c.date_opened));
      formD1.getTextField(`value${suffix}`).setText(getAssetValue(c));
      // Balance field (formatted to two decimals)
      const bal1 = parseFloat(c.balance) || 0;
      formD1.getTextField(`balance${suffix}`).setText(bal1.toFixed(2));
      // Lien type checkboxes
      ['agreement', 'statutory', 'judgment', 'other'].forEach(type => {
        const field = type === 'agreement'
          ? `agreement${suffix}`
          : `${type}_lien${suffix}`;
        const box = formD1.getCheckBox(field);
        if (c.nature_of_lien === type) box.check();
        else box.uncheck();
      });
      // Dispute status (only check if not 'none')
      if (c.dispute_status && c.dispute_status !== 'none') {
        formD1.getCheckBox(`${c.dispute_status}${suffix}`).check();
      }
      // Additional flags
      if (c.community_debt)      formD1.getCheckBox(`community_debt${suffix}`).check();

      // Who owes checkbox mapping
      let whoField = null;
      switch (c.who_owes) {
        case 'debtor1':
          whoField = 'debtor1_owes';
          break;
        case 'debtor2':
          whoField = 'debtor2_owes';
          break;
        case 'joint':
          whoField = 'joint_owes';
          break;
        case 'debtor1_third':
        case 'debtor2_third':
          whoField = 'third_owes';
          break;
        case 'authorized':
          whoField = 'authorized_owes';
          break;
      }
      if (whoField) {
        formD1.getCheckBox(`${whoField}${suffix}`).check();
      }

      // Compute unsecured portion = max(balance - assetValue, 0)
      const assetVal1 = parseFloat(getAssetValue(c)) || 0;
      const unsecured1 = Math.max(bal1 - assetVal1, 0).toFixed(2);
      formD1.getTextField(`unsecured_portion${suffix}`).setText(unsecured1);
    });

    // Calculate page total for first two creditors
    const pageTotal1 = securedDebts.slice(0,2)
      .reduce((sum, c) => sum + (parseFloat(c.balance) || 0), 0);
    formD1.getTextField('amount_this_page').setText(pageTotal1.toFixed(2));
    // Initialize running total for amount_total_so_far
    //formD1.getTextField('amount_total_so_far').setText(pageTotal1.toFixed(2));
    // TODO: calculate and set amount_this_page and page_total
    // Total D pages on page 1
    formD1.getTextField('page_total').setText(totalDPages.toString());

    // Hide unchecked checkboxes by moving them to top-right
    const width1 = page1.getWidth();
    const height1 = page1.getHeight();
    formD1.getFields().forEach(field => {
      if (field instanceof PDFCheckBox && !field.isChecked()) {
        field.acroField.getWidgets().forEach(widget => {
          const dict = widget.acroAnnotation?.dict || widget.dict;
          // move widget off-page by setting its Rect to a small area beyond the page bounds
          dict.set(
            PDFName.of('Rect'),
            d1Doc.context.obj([
              width1 + 5,   // lower-left x just outside page
              height1 + 5,  // lower-left y just outside page
              width1 + 15,  // upper-right x a bit further out
              height1 + 15  // upper-right y a bit further out
            ])
          );
        });
      }
    });
    // Flatten form fields before copying page
    formD1.flatten();

    // Add the filled page to the merged PDF
    const [d1Page] = await merged.copyPages(d1Doc, [0]);
    merged.addPage(d1Page);
    // b) Continuation pages: for secured debts beyond the first two, 3 per page
    const contD = securedDebts.slice(2);
    // running total starts with first two balances
    let runningTotal = 0;
    if (securedDebts[0]) runningTotal += parseFloat(securedDebts[0].balance);
    if (securedDebts[1]) runningTotal += parseFloat(securedDebts[1].balance);

    for (let i = 0; i < contD.length; i += 3) {
      const chunk = contD.slice(i, i + 3);
      const d2Bytes = await fetch(D_PAGE2).then(r => r.arrayBuffer());
      const d2Doc = await PDFDocument.load(d2Bytes);
      const formD2 = d2Doc.getForm();

      // Fill up to three creditors per page
      let pageTotal = 0;
      chunk.forEach((c, idx) => {
        const n = idx + 1; // 1,2,3
        const suffix = n === 1 ? '' : `_${n}`;
        // Creditor numbering field (e.g., 2.3, 2.4, 2.5)
        const creditorIndex = i + idx + 3;  // global index starting from 3
        formD2.getTextField(`creditor_count${suffix}`).setText(`2.${creditorIndex}`);

        // Lien type checkboxes
        ['agreement', 'statutory', 'judgment', 'other'].forEach(type => {
          const field = type === 'agreement'
            ? `agreement${suffix}`
            : `${type}_lien${suffix}`;
          const box = formD2.getCheckBox(field);
          if (c.nature_of_lien === type) box.check();
          else box.uncheck();
        });
        formD2.getTextField(`account_name${suffix}`).setText(c.account_name);
        // Only last 4 digits of account number
        const acctNum2 = c.account_number || '';
        formD2.getTextField(`account_number${suffix}`).setText(acctNum2.slice(-4));
        formD2.getTextField(`property_description${suffix}`).setText(getPropertyDescription(c));
        formD2.getTextField(`street1${suffix}`).setText(c.street1);
        formD2.getTextField(`street2${suffix}`).setText(c.street2 || '');
        formD2.getTextField(`city${suffix}`).setText(c.city);
        formD2.getTextField(`state${suffix}`).setText(c.state);
        formD2.getTextField(`zip_code${suffix}`).setText(c.zip_code);
        if (shouldSetDate(c.date_opened))
        formD2.getTextField(`date_incurred${suffix}`).setText(formatDate(c.date_opened));
        formD2.getTextField(`value${suffix}`).setText(getAssetValue(c));
        formD2.getTextField(`balance${suffix}`).setText(c.balance);

        // Who owes checkbox mapping
        let whoField2 = null;
        switch (c.who_owes) {
          case 'debtor1':
            whoField2 = 'debtor1_owes';
            break;
          case 'debtor2':
            whoField2 = 'debtor2_owes';
            break;
          case 'joint':
            whoField2 = 'joint_owes';
            break;
          case 'debtor1_third':
          case 'debtor2_third':
            whoField2 = 'third_owes';
            break;
          case 'authorized':
            whoField2 = 'authorized_owes';
            break;
        }
        if (whoField2) {
          formD2.getCheckBox(`${whoField2}${suffix}`).check();
        }

        // Compute unsecured portion = max(balance - assetValue, 0)
        const bal2 = parseFloat(c.balance) || 0;
        const assetVal2 = parseFloat(getAssetValue(c)) || 0;
        const unsecured2 = Math.max(bal2 - assetVal2, 0).toFixed(2);
        formD2.getTextField(`unsecured_portion${suffix}`).setText(unsecured2);

        // Lien type: treat auto or mortgage as agreement

        // Dispute status (only check if not 'none')
        if (c.dispute_status && c.dispute_status !== 'none') {
          formD2.getCheckBox(`${c.dispute_status}${suffix}`).check();
        }

        // Additional flags
        if (c.community_debt)    formD2.getCheckBox(`community_debt${suffix}`).check();

        pageTotal += parseFloat(c.balance);
        runningTotal += parseFloat(c.balance);
      });

      // Fill page-level totals
      formD2.getTextField('amount_this_page').setText(pageTotal.toFixed(2));
      formD2.getTextField('amount_total_so_far').setText(runningTotal.toFixed(2));
      formD2.getTextField('this_page_number').setText((Math.floor(i / 3) + 2).toString());
      // Total D pages on continuation
      formD2.getTextField('page_total').setText(totalDPages.toString());
      // Debtor1 name on continuation page
      if (primaryDebtor) {
        formD2.getTextField('debtor1').setText(
          [primaryDebtor.first_name, primaryDebtor.second_name, primaryDebtor.last_name]
            .filter(Boolean).join(' ')
        );
      }

      // Hide unchecked checkboxes on continuation page
      const page2 = d2Doc.getPage(0);
      const w2 = page2.getWidth();
      const h2 = page2.getHeight();
      formD2.getFields().forEach(field => {
      if (field instanceof PDFCheckBox && !field.isChecked()) {
        field.acroField.getWidgets().forEach(widget => {
          const dict = widget.acroAnnotation?.dict || widget.dict;
          // move widget off-page by setting its Rect to an out-of-bounds box
          dict.set(
            PDFName.of('Rect'),
            d2Doc.context.obj([
              w2 + 5,   // lower-left x just outside page
              h2 + 5,  // lower-left y just outside page
              w2 + 15,  // upper-right x a bit further out
              h2 + 15  // upper-right y a bit further out
            ])
          );
        });
      }
    });
      // Flatten form fields before copying page
      formD2.flatten();

      // Add the filled continuation page
      const [d2Page] = await merged.copyPages(d2Doc, [0]);
      merged.addPage(d2Page);
    }

    // c) Notification pages for secured debts requiring additional notice (6 per page)
    for (let i = 0; i < notifySecured.length; i += 6) {
      const chunk = notifySecured.slice(i, i + 6);
      const d3Bytes = await fetch(D_PAGE3).then(r => r.arrayBuffer());
      const d3Doc = await PDFDocument.load(d3Bytes);
      const formD3 = d3Doc.getForm();

      // Total D pages on notification page
      formD3.getTextField('page_total').setText(totalDPages.toString());
      // Debtor1 name on notification page
      if (primaryDebtor) {
        formD3.getTextField('debtor1').setText(
          [primaryDebtor.first_name, primaryDebtor.second_name, primaryDebtor.last_name]
            .filter(Boolean).join(' ')
        );
      }

      // Fill up to six notification slots
      chunk.forEach((c, idx) => {
        const n = idx + 1; // n = 1–6
        const suffix = n === 1 ? '' : `_${n}`;
        const acctNum1 = c.account_number || '';
        formD3.getTextField(`notified_count${suffix}`).setText(n.toString());
        formD3.getTextField(`account_name${suffix}`).setText(c.account_name);
        formD3.getTextField(`street1${suffix}`).setText(c.street1 + (c.street2 ? ' ' + c.street2 : ''));
        formD3.getTextField(`city${suffix}`).setText(c.city);
        formD3.getTextField(`state${suffix}`).setText(c.state);
        formD3.getTextField(`zip_code${suffix}`).setText(c.zip_code);
        formD3.getTextField(`account_number${suffix}`).setText(acctNum1.slice(-4));
      });

      // Set this_page_number for notification pages
      const notifPageNum = contCount + Math.floor(i / 6) + 2;
      formD3.getTextField('this_page_number').setText(notifPageNum.toString());

      // Hide unchecked checkboxes on notification page
      const page3 = d3Doc.getPage(0);
      const w3 = page3.getWidth();
      const h3 = page3.getHeight();
      formD3.getFields().forEach(field => {
      if (field instanceof PDFCheckBox && !field.isChecked()) {
        field.acroField.getWidgets().forEach(widget => {
          const dict = widget.acroAnnotation?.dict || widget.dict;
          // move widget off-page by setting its Rect to an out-of-bounds box
          dict.set(
            PDFName.of('Rect'),
            d3Doc.context.obj([  w3 - 20, h3 - 20, 10, 10 ])
          );
        });
      }
    });
      // Flatten form fields before copying page
      formD3.flatten();

      // Add the filled notification page
      const [d3Page] = await merged.copyPages(d3Doc, [0]);
      merged.addPage(d3Page);
    }
  }

  if (selectedSchedules.includes('EF') || selectedSchedules.includes('all')) {
    // 2. Schedule E – Page 1 (first two priority creditors)
    {
        // Load the PDF template
        const e1Bytes = await fetch(E_PAGE1).then(r => r.arrayBuffer());
        const e1Doc   = await PDFDocument.load(e1Bytes);
        const pageE1  = e1Doc.getPage(0);
        const formE1  = e1Doc.getForm();
        if (data.amended) formE1.getCheckBox('amended').check()
        // Case metadata'
      if (data.caseInfo.case_number)
        formE1.getTextField('case_number').setText(data.caseInfo.case_number);
        formE1.getTextField('district').setText('Eastern District of Tennessee');
        
        // Debtor names: primary = debtor1, secondary = debtor2 (only if filing)
        if (primaryDebtor) {
          formE1.getTextField('debtor1').setText(
            [primaryDebtor.first_name, primaryDebtor.second_name, primaryDebtor.last_name]
              .filter(Boolean).join(' ')
          );
        }
        if (secondaryDebtor) {
          formE1.getTextField('debtor2').setText(
            [secondaryDebtor.first_name, secondaryDebtor.second_name, secondaryDebtor.last_name]
              .filter(Boolean).join(' ')
          );
        }

        // Priority debts array
        const prio = data.debtsE.filter(d => d.debt_type === 'priority' && !d.additional_notified && !d.who_owes !== "authorized");
        // Total E pages (1 main + continuation)
        const totalEPages = 1 + Math.ceil(Math.max(prio.length - 2, 0) / 3);

        // Fill the first two entries
        prio.slice(0,2).forEach((c, idx) => {
        const suffix = idx === 0 ? '' : '_2';
        // Creditor info
        
        formE1.getTextField(`account_name${suffix}`).setText(c.account_name);
        const acct = (c.account_number||'').slice(-4);
        formE1.getTextField(`account_number${suffix}`).setText(acct);
        formE1.getTextField(`street1${suffix}`).setText(c.street1);
        formE1.getTextField(`street2${suffix}`).setText(c.street2||'');
        formE1.getTextField(`city${suffix}`).setText(c.city);
        formE1.getTextField(`state${suffix}`).setText(c.state);
        formE1.getTextField(`zip_code${suffix}`).setText(c.zip_code);
        if (shouldSetDate(c.date_opened))
        formE1.getTextField(`date_incurred${suffix}`).setText(formatDate(c.date_opened));

        // Balance
        const bal = (parseFloat(c.balance)||0).toFixed(2);
        formE1.getTextField(`balance${suffix}`).setText(bal);

        // Priority amount
        const prAmt = (parseFloat(c.priority_amount)||0).toFixed(2);
        formE1.getTextField(`priority_amount${suffix}`).setText(prAmt);

        // Flags
        if (c.dispute_status && c.dispute_status !== 'none') {
            formE1.getCheckBox(`${c.dispute_status}${suffix}`).check();
        }
        const pType1 = (c.priority_amount !== undefined ? (c.priority_type || '') : '').toLowerCase();
        if (pType1 === 'dso')         formE1.getCheckBox(`dso${suffix}`).check();
        if (pType1 === 'intoxicated') formE1.getCheckBox(`intox${suffix}`).check();
        if (pType1 === 'tax')         formE1.getCheckBox(`tax${suffix}`).check();
        if (c.community_debt)         formE1.getCheckBox(`community_debt${suffix}`).check();

        // Offset yes/no
        if (c.subject_to_offset) formE1.getCheckBox(`offset_yes${suffix}`).check();
        else                      formE1.getCheckBox(`offset_no${suffix}`).check();

        // nonpriority_amount = max(balance - priority_amount, 0)
        const bal1E = parseFloat(c.balance) || 0;
        const prAmt1 = parseFloat(c.priority_amount) || 0;
        const nonPr1 = Math.max(bal1E - prAmt1, 0).toFixed(2);
        formE1.getTextField(`nonpriority_amount${suffix}`).setText(nonPr1);

        // “Other” and description
        if (c.other) {
            formE1.getCheckBox(`other${suffix}`).check();
            formE1.getTextField(`other_description${suffix}`)
                .setText(c.account_type||'');
        }

        // Who owes mapping
        let whoField = '';
        switch (c.who_owes) {
            case 'debtor1':      whoField = 'debtor1_owes'; break;
            case 'debtor2':      whoField = 'debtor2_owes'; break;
            case 'joint':        whoField = 'joint_owes';  break;
            case 'debtor1_third':
            case 'debtor2_third': whoField = 'third_owes';  break;
            case 'authorized':   whoField = 'authorized_owes'; break;
        }
        if (whoField) formE1.getCheckBox(whoField + suffix).check();
        });

        // Check priority_yes or priority_no once for the entire form
        const priorityDebts = prio;
        if (priorityDebts.length > 0) {
          formE1.getCheckBox('priority_yes').check();
          formE1.getCheckBox('priority_no').uncheck();
        } else {
          formE1.getCheckBox('priority_no').check();
          formE1.getCheckBox('priority_yes').uncheck();
        }
    
        // E1 page numbering
        formE1.getTextField('page_total').setText(totalEPages.toString());

        // Hide unchecked, flatten, merge
        const wE = pageE1.getWidth(), hE = pageE1.getHeight();
        formE1.getFields().forEach(field => {
          if (field instanceof PDFCheckBox && !field.isChecked()) {
            field.acroField.getWidgets().forEach(widget => {
              const dict = widget.acroAnnotation?.dict || widget.dict;
              // move widget off-page by setting its Rect to a small area beyond the page bounds
              dict.set(
                PDFName.of('Rect'),
                e1Doc.context.obj([
                  wE + 5,   // lower-left x just outside page
                  hE + 5,  // lower-left y just outside page
                  wE + 15,  // upper-right x a bit further out
                  hE + 15  // upper-right y a bit further out
                ])
              );
            });
          }
        });

        formE1.getTextField('page_total').setText(totalSharedPages.toString());
        sharedPageCount++;
        formE1.flatten();
        const [e1Page] = await merged.copyPages(e1Doc, [0]);
        merged.addPage(e1Page);
    }

    // 3. Schedule E – Continuation pages (priority creditors beyond first two, 3 per page)
    const contE = data.debtsE.slice(2);
    for (let i = 0; i < contE.length; i += 3) {
      const chunk = contE.slice(i, i + 3);
      const e2Bytes = await fetch(E_PAGE2).then(r => r.arrayBuffer());
      const e2Doc = await PDFDocument.load(e2Bytes);
      const pageE2 = e2Doc.getPage(0);
      const formE2 = e2Doc.getForm();

      // Fill up to three creditors per page
      let pageTotalE2 = 0;
      chunk.forEach((c, idx) => {
        const n = idx + 1; // 1,2,3
        const suffix = n === 1 ? '' : `_${n}`;
        // Creditor number: 3.<global index>
        const creditorIndex = i + idx + 3;
        formE2.getTextField(`creditor_count${suffix}`).setText(`2.${creditorIndex}`);

        formE2.getTextField(`account_name${suffix}`).setText(c.account_name);
        const acct = (c.account_number || '').slice(-4);
        formE2.getTextField(`account_number${suffix}`).setText(acct);
        formE2.getTextField(`street1${suffix}`).setText(c.street1);
        formE2.getTextField(`street2${suffix}`).setText(c.street2 || '');
        formE2.getTextField(`city${suffix}`).setText(c.city);
        formE2.getTextField(`state${suffix}`).setText(c.state);
        formE2.getTextField(`zip_code${suffix}`).setText(c.zip_code);
        if (shouldSetDate(c.date_opened))
        formE2.getTextField(`date_incurred${suffix}`).setText(formatDate(c.date_opened));
        const balE = parseFloat(c.balance) || 0;
        formE2.getTextField(`balance${suffix}`).setText(balE.toFixed(2));

        const prAmtE = parseFloat(c.priority_amount) || 0;
        formE2.getTextField(`priority_amount${suffix}`).setText(prAmtE.toFixed(2));


        // nonpriority_amount = max(balance - priority_amount, 0)
        const nonPrE = Math.max(balE - prAmtE, 0).toFixed(2);
        formE2.getTextField(`nonpriority_amount${suffix}`).setText(nonPrE);

        // Flags
        if (c.dispute_status && c.dispute_status !== 'none') {
            formE2.getCheckBox(`${c.dispute_status}${suffix}`).check();
          }
        const pType = (c.priority_type || '').toLowerCase();
        if (pType === 'dso')         formE2.getCheckBox(`dso${suffix}`).check();
        if (pType === 'intoxicated') formE2.getCheckBox(`intox${suffix}`).check();
        if (pType === 'tax')         formE2.getCheckBox(`tax${suffix}`).check();
        if (c.community_debt)         formE2.getCheckBox(`community_debt${suffix}`).check();

        if (c.subject_to_offset) formE2.getCheckBox(`offset_yes${suffix}`).check();
        else                      formE2.getCheckBox(`offset_no${suffix}`).check();

        if (c.other) {
          formE2.getCheckBox(`other${suffix}`).check();
          formE2.getTextField(`other_description${suffix}`).setText(c.account_type||'');
        }

        // Who owes mapping
        let whoF2 = '';
        switch (c.who_owes) {
          case 'debtor1': whoF2 = 'debtor1_owes'; break;
          case 'debtor2': whoF2 = 'debtor2_owes'; break;
          case 'joint':   whoF2 = 'joint_owes';  break;
          case 'debtor1_third':
          case 'debtor2_third': whoF2 = 'third_owes'; break;
          case 'authorized': whoF2 = 'authorized_owes'; break;
        }
        if (whoF2) formE2.getCheckBox(whoF2+suffix).check();

        pageTotalE2 += prAmtE;
      });
      if (primaryDebtor) {
        formE2.getTextField('debtor1').setText(
          [primaryDebtor.first_name, primaryDebtor.second_name, primaryDebtor.last_name]
            .filter(Boolean).join(' ')
        );
      }

      // Hide unchecked and flatten
      const wE2 = pageE2.getWidth(), hE2 = pageE2.getHeight();
    formE2.getFields().forEach(field => {
      if (field instanceof PDFCheckBox && !field.isChecked()) {
        field.acroField.getWidgets().forEach(widget => {
          const dict = widget.acroAnnotation?.dict || widget.dict;
          // move widget off-page by setting its Rect to a small area beyond the page bounds
          dict.set(
            PDFName.of('Rect'),
            e2Doc.context.obj([
              wE2 + 5,   // lower-left x just outside page
              hE2 + 5,  // lower-left y just outside page
              wE2 + 15,  // upper-right x a bit further out
              hE2 + 15  // upper-right y a bit further out
            ])
          );
        });
      }
    });
      formE2.getTextField('page_count').setText(sharedPageCount.toString());
      formE2.getTextField('page_total').setText(totalSharedPages.toString());
      sharedPageCount++;

      formE2.flatten();
      const [e2Page] = await merged.copyPages(e2Doc,[0]);
      merged.addPage(e2Page);
    }

    // 4. Schedule F – Page 1 (first three nonpriority unsecured creditors)
    {
      const f1Bytes = await fetch(F_PAGE1).then(r => r.arrayBuffer());
      const f1Doc = await PDFDocument.load(f1Bytes);
      const pageF1 = f1Doc.getPage(0);
      const formF1 = f1Doc.getForm();

      // Debtor1 name
      if (primaryDebtor) {
        formF1.getTextField('debtor1').setText(
          [primaryDebtor.first_name, primaryDebtor.second_name, primaryDebtor.last_name]
            .filter(Boolean).join(' ')
        );
      }

      // Unsecured yes/no (any unsecured debts?)
      if (data.debtsF.length > 0) {
        formF1.getCheckBox('unsecured_yes').check();
        formF1.getCheckBox('unsecured_no').uncheck();
      } else {
        formF1.getCheckBox('unsecured_no').check();
        formF1.getCheckBox('unsecured_yes').uncheck();
      }

      // First three unsecured debts
      const nonprio = data.debtsF.filter(d => d.debt_type === 'unsecured' && !d.additional_notified && d.who_owes !== "authorized");
      nonprio.slice(0,3).forEach((c, idx) => {
        const suffix = idx === 0 ? '' : `_${idx+1}`;
        if (c.dispute_status && c.dispute_status !== 'none') {
          formF1.getCheckBox(`${c.dispute_status}${suffix}`).check();
        }

        formF1.getTextField(`account_name${suffix}`).setText(c.account_name);
        formF1.getTextField(`account_number${suffix}`).setText((c.account_number||'').slice(-4));
        formF1.getTextField(`street1${suffix}`).setText(c.street1);
        formF1.getTextField(`city${suffix}`).setText(c.city);
        formF1.getTextField(`state${suffix}`).setText(c.state);
        formF1.getTextField(`zip_code${suffix}`).setText(c.zip_code);
        const bal = (parseFloat(c.balance)||0).toFixed(2);
        formF1.getTextField(`balance${suffix}`).setText(bal);
        // Nonpriority claim checkboxes by account_type
        const at = (c.account_type||'').toLowerCase();
        ['student_loan','divorce','pension','other'].forEach(type => {
          const box = formF1.getCheckBox(type + suffix);
          if (type === 'student_loan' && at.includes('student')) box.check(); else if
          (type==='divorce' && at.includes('divorce')) box.check(); else if
          (type==='pension' && at.includes('pension')) box.check(); else if
          (!['student','divorce','pension'].some(k=>at.includes(k)) && type==='other') {
            box.check();
            formF1.getTextField(`other_description${suffix}`).setText(c.account_type||'');
          } else box.uncheck();
        });
        // Date incurred
        if (shouldSetDate(c.date_opened))
        formF1.getTextField(`date_incurred${suffix}`).setText(formatDate(c.date_opened));
        // Offset yes/no
        if (c.subject_to_offset) formF1.getCheckBox(`offset_yes${suffix}`).check();
        else                      formF1.getCheckBox(`offset_no${suffix}`).check();
        // Who owes mapping
        let whoF = '';
        switch(c.who_owes) {
          case 'debtor1': whoF = 'debtor1_owes'; break;
          case 'debtor2': whoF = 'debtor2_owes'; break;
          case 'joint':   whoF = 'joint_owes';  break;
          case 'debtor1_third':
          case 'debtor2_third': whoF = 'third_owes'; break;
          case 'authorized': whoF = 'authorized_owes'; break;
        }
        if (whoF) formF1.getCheckBox(whoF + suffix).check();
      });

      // Hide unchecked, flatten, merge
      const wF1 = pageF1.getWidth(), hF1 = pageF1.getHeight();
      formF1.getFields().forEach(field => {
        if (field instanceof PDFCheckBox && !field.isChecked()) {
          field.acroField.getWidgets().forEach(widget => {
            const dict = widget.acroAnnotation?.dict || widget.dict;
            // move widget off-page by setting its Rect to a small area beyond the page bounds
            dict.set(
              PDFName.of('Rect'),
              f1Doc.context.obj([
                wF1 + 5,   // lower-left x just outside page
                hF1 + 5,  // lower-left y just outside page
                wF1 + 15,  // upper-right x a bit further out
                hF1 + 15  // upper-right y a bit further out
              ])
            );
          });
        }
      });
      formF1.getTextField('page_count').setText(sharedPageCount.toString());
      formF1.getTextField('page_total').setText(totalSharedPages.toString());
      sharedPageCount++;
      formF1.flatten();
      merged.addPage((await merged.copyPages(f1Doc,[0]))[0]);
    }

    // 5. Schedule F – Continuation pages (nonpriority debts 4+)
    {
      const nonprio = data.debtsF.filter(d => d.debt_type === 'unsecured' && !d.additional_notified && d.who_owes !== "authorized");
      for (let i=3; i<nonprio.length; i+=3) {
        const chunk = nonprio.slice(i, i+3);
        const f2Bytes = await fetch(F_PAGE2).then(r=>r.arrayBuffer());
        const f2Doc = await PDFDocument.load(f2Bytes);
        const pageF2 = f2Doc.getPage(0);
        const formF2 = f2Doc.getForm();

        // Creditor numbering 4.4,4.5,4.6...
        chunk.forEach((c, idx) => {
          const n = idx+1, suffix = n===1?'':`_${n}`;
          const creditorIndex = i + idx + 1;
          if (c.dispute_status && c.dispute_status !== 'none') {
            formF2.getCheckBox(`${c.dispute_status}${suffix}`).check();
          }
          formF2.getTextField(`creditor_count${suffix}`).setText(`4.${creditorIndex}`);
          formF2.getTextField(`account_name${suffix}`).setText(c.account_name);
          formF2.getTextField(`account_number${suffix}`).setText((c.account_number||'').slice(-4));
          formF2.getTextField(`street1${suffix}`).setText(c.street1);
          formF2.getTextField(`city${suffix}`).setText(c.city);
          formF2.getTextField(`state${suffix}`).setText(c.state);
          formF2.getTextField(`zip_code${suffix}`).setText(c.zip_code);
          const bal = (parseFloat(c.balance)||0).toFixed(2);
          formF2.getTextField(`balance${suffix}`).setText(bal);
          // nonpriority claim checkboxes
          const at = (c.account_type||'').toLowerCase();
          ['student_loan','divorce','pension','other'].forEach(type => {
            const box = formF2.getCheckBox(type + suffix);
            if (type==='student_loan'&&at.includes('student')) box.check(); else
            if (type==='divorce'&&at.includes('divorce')) box.check(); else
            if (type==='pension'&&at.includes('pension')) box.check(); else
            if (!['student','divorce','pension'].some(k=>at.includes(k))&&type==='other') {
              box.check();
              formF2.getTextField(`other_description${suffix}`).setText(c.account_type||'');
            } else box.uncheck();
          });
          // Date incurred
          if (shouldSetDate(c.date_opened))
          formF2.getTextField(`date_incurred${suffix}`).setText(formatDate(c.date_opened));
          // Offset yes/no
          if (c.subject_to_offset) formF2.getCheckBox(`offset_yes${suffix}`).check();
          else                      formF2.getCheckBox(`offset_no${suffix}`).check();
          // Who owes mapping
          let whoF = '';
          switch(c.who_owes) {
            case 'debtor1': whoF = 'debtor1_owes'; break;
            case 'debtor2': whoF = 'debtor2_owes'; break;
            case 'joint':   whoF = 'joint_owes';  break;
            case 'debtor1_third':
            case 'debtor2_third': whoF = 'third_owes'; break;
            case 'authorized': whoF = 'authorized_owes'; break;
          }
          if (whoF) formF2.getCheckBox(whoF + suffix).check();
        });

        // Debtor1 name on continuation page
        if (primaryDebtor) {
          formF2.getTextField('debtor1').setText(
            [primaryDebtor.first_name, primaryDebtor.second_name, primaryDebtor.last_name]
              .filter(Boolean).join(' ')
          );
        }

        // Hide unchecked, flatten, merge
        const wF2 = pageF2.getWidth(), hF2 = pageF2.getHeight();
        formF2.getFields().forEach(field => {
          if (field instanceof PDFCheckBox && !field.isChecked()) {
            field.acroField.getWidgets().forEach(widget => {
              const dict = widget.acroAnnotation?.dict || widget.dict;
              // move widget off-page by setting its Rect to a small area beyond the page bounds
              dict.set(
                PDFName.of('Rect'),
                f2Doc.context.obj([
                  wF2 + 5,   // lower-left x just outside page
                  hF2 + 5,  // lower-left y just outside page
                  wF2 + 15,  // upper-right x a bit further out
                  hF2 + 15  // upper-right y a bit further out
                ])
              );
            });
          }
        });
        formF2.getTextField('page_count').setText(sharedPageCount.toString());
        formF2.getTextField('page_total').setText(totalSharedPages.toString());
        sharedPageCount++;
        formF2.flatten();
        merged.addPage((await merged.copyPages(f2Doc,[0]))[0]);
      }
    }

    // 6. Schedule EF – Additional Notices (7 per page)
    {
      const notifyAll = [...data.debtsE, ...data.debtsF].filter(d=>d.additional_notified);
      for (let i=0; i<notifyAll.length; i+=7) {
        const chunk = notifyAll.slice(i, i+7);
        const efBytes = await fetch(EF_LABELS).then(r=>r.arrayBuffer());
        const efDoc = await PDFDocument.load(efBytes);
        const pageEF = efDoc.getPage(0);
        const formEF = efDoc.getForm();

        // Fill 7 slots
        chunk.forEach((c, idx) => {
          const n=idx+1, suffix=n===1?'':`_${n}`;
          formEF.getTextField(`account_name${suffix}`).setText(c.account_name);
          formEF.getTextField(`street1${suffix}`).setText(c.street1);
          formEF.getTextField(`city${suffix}`).setText(c.city);
          formEF.getTextField(`state${suffix}`).setText(c.state);
          formEF.getTextField(`zip_code${suffix}`).setText(c.zip_code);
          formEF.getTextField(`account_number${suffix}`).setText((c.account_number||'').slice(-4));
          // Priority vs Unsecured box
          if (c.debt_type === 'priority') {
            formEF.getCheckBox(`priority${suffix}`).check();
          } else {
            formEF.getCheckBox(`unsecured${suffix}`).check();
          }
        });

        // Hide unchecked, flatten, merge
        const wEF=pageEF.getWidth(), hEF=pageEF.getHeight();
        formEF.getFields().forEach(field => {
          if (field instanceof PDFCheckBox && !field.isChecked()) {
            field.acroField.getWidgets().forEach(widget => {
              const dict = widget.acroAnnotation?.dict || widget.dict;
              // move widget off-page by setting its Rect to a small area beyond the page bounds
              dict.set(
                PDFName.of('Rect'),
                efDoc.context.obj([
                  wEF + 5,   // lower-left x just outside page
                  hEF + 5,  // lower-left y just outside page
                  wEF + 15,  // upper-right x a bit further out
                  hEF + 15  // upper-right y a bit further out
                ])
              );
            });
          }
        });
        formEF.getTextField('page_count').setText(sharedPageCount.toString());
        formEF.getTextField('page_total').setText(totalSharedPages.toString());
        sharedPageCount++;
        formEF.flatten();
        merged.addPage((await merged.copyPages(efDoc,[0]))[0]);
      }
    }

    // 7. Schedule EF – Totals page
    {
      const tfBytes = await fetch(EF_TOTALS).then(r=>r.arrayBuffer());
      const tfDoc = await PDFDocument.load(tfBytes);
      const pageTF = tfDoc.getPage(0);
      const formTF = tfDoc.getForm();

      formTF.getTextField('debtor1').setText(
        [primaryDebtor.first_name,primaryDebtor.second_name,primaryDebtor.last_name]
          .filter(Boolean).join(' ')
      );
      if (data.caseInfo.case_number)
      formTF.getTextField('case_number').setText(data.caseInfo.case_number);

      // Totals for priority types
      const pr = data.debtsE.filter(d=>d.debt_type==='priority');
      const sumBy = (arr, field)=>arr.reduce((s,c)=>s+(parseFloat(c[field])||0),0);
      formTF.getTextField('total_dso').setText(
        sumBy(pr.filter(d=>(d.priority_type||'').toLowerCase()==='dso'),'priority_amount').toFixed(2)
      );
      formTF.getTextField('total_tax').setText(
        sumBy(pr.filter(d=>(d.priority_type||'').toLowerCase()==='tax'),'priority_amount').toFixed(2)
      );
      formTF.getTextField('total_intox').setText(
        sumBy(pr.filter(d=>(d.priority_type||'').toLowerCase()==='intoxicated'),'priority_amount').toFixed(2)
      );
      formTF.getTextField('total_other_priority').setText(
        sumBy(pr.filter(d=>{
          const t=(d.priority_type||'').toLowerCase();
          return t && !['dso','tax','intoxicated'].includes(t);
        }),'priority_amount').toFixed(2)
      );
      formTF.getTextField('total_priority').setText(sumBy(pr,'priority_amount').toFixed(2));

      // Totals for unsecured types
      const un = data.debtsF.filter(d=>d.debt_type==='unsecured' && !d.additional_notified);
      const filterSum=(type)=>sumBy(un.filter(d=>{
        const at=(d.account_type||'').toLowerCase();
        if(type==='student_loan') return at.includes('student');
        if(type==='divorce') return at.includes('divorce');
        if(type==='pension') return at.includes('pension');
        if(type==='other') return !['student','divorce','pension'].some(k=>at.includes(k));
        return false;
      }),'balance');
      formTF.getTextField('total_student_loan').setText(filterSum('student_loan').toFixed(2));
      formTF.getTextField('total_divorce').setText(filterSum('divorce').toFixed(2));
      formTF.getTextField('total_pension').setText(filterSum('pension').toFixed(2));
      const totalPriorityBalance = sumBy(pr, 'balance');
      const totalPriorityAmount = sumBy(pr, 'priority_amount');
      const otherUnsecured = filterSum('other')
      const otherUnsecuredAdjusted = otherUnsecured + (totalPriorityBalance-totalPriorityAmount)
      formTF.getTextField('total_other_unsecured').setText(otherUnsecuredAdjusted.toFixed(2));

      // Compute total priority balance minus priority amounts

      // Total unsecured plus priority balance overage
      const unsecuredBase = sumBy(un, 'balance');
      const adjustedUnsecured = unsecuredBase + (totalPriorityBalance - totalPriorityAmount);
      formTF.getTextField('total_unsecured').setText(adjustedUnsecured.toFixed(2));

      // Shared pagination for EF-2 totals
      formTF.getTextField('page_count').setText(sharedPageCount.toString());
      formTF.getTextField('page_total').setText(totalSharedPages.toString());
      sharedPageCount++;

      // Hide unchecked, flatten, merge
      const wTF=pageTF.getWidth(), hTF=pageTF.getHeight();
      formTF.getFields().forEach(field => {
        if (field instanceof PDFCheckBox && !field.isChecked()) {
          field.acroField.getWidgets().forEach(widget => {
            const dict = widget.acroAnnotation?.dict || widget.dict;
            // move widget off-page by setting its Rect to a small area beyond the page bounds
            dict.set(
              PDFName.of('Rect'),
              tfDoc.context.obj([
                wTF + 5,   // lower-left x just outside page
                hTF + 5,  // lower-left y just outside page
                wTF + 15,  // upper-right x a bit further out
                hTF + 15  // upper-right y a bit further out
              ])
            );
          });
        }
      });
      formTF.flatten();
      merged.addPage((await merged.copyPages(tfDoc,[0]))[0]);
    }
  } // End of Schedule EF pages
  // Start of Schedule G
  if (selectedSchedules.includes('G') || selectedSchedules.includes('all')){
            // Executory COntract debts array
        sharedPageCount = 1;
        const leases = data.debtsG.filter(d => d.debt_type === 'executory' && !d.additional_notified);
        // Total E pages (1 main + continuation)
        const totalGPages = 1 + Math.ceil(Math.max(leases.length - 5, 0) / 8);
    {

        // Load the PDF template
        const g1Bytes = await fetch(G_PAGE1).then(r => r.arrayBuffer());
        const g1Doc   = await PDFDocument.load(g1Bytes);
        const pageG1  = g1Doc.getPage(0);
        const formG1  = g1Doc.getForm();
        if (data.amended) formG1.getCheckBox('amended').check()
        // Case metadata'
              if (leases.length > 0) {
          formG1.getCheckBox('yes_lease').check()
        }
        else{
          formG1.getCheckBox('no_lease').check()
        }
      if (data.caseInfo.case_number)
        formG1.getTextField('case_number').setText(data.caseInfo.case_number);
        formG1.getTextField('district').setText('Eastern District of Tennessee');
        
        // Debtor names: primary = debtor1, secondary = debtor2 (only if filing)
        if (primaryDebtor) {
          formG1.getTextField('Debtor 1').setText(
            [primaryDebtor.first_name, primaryDebtor.second_name, primaryDebtor.last_name]
              .filter(Boolean).join(' ')
          );
        }
        if (secondaryDebtor) {
          formG1.getTextField('Debtor 2').setText(
            [secondaryDebtor.first_name, secondaryDebtor.second_name, secondaryDebtor.last_name]
              .filter(Boolean).join(' ')
          );
        }



        // Fill the first two entries
        leases.slice(0,5).forEach((c, idx) => {
        const suffix = idx = `_${idx + 1}`
        // Creditor info
        
        formG1.getTextField(`account_name${suffix}`).setText(c.account_name);
        formG1.getTextField(`street${suffix}`).setText(`${c.street1} ${c.street2}`);
        formG1.getTextField(`city${suffix}`).setText(c.city);
        formG1.getTextField(`state${suffix}`).setText(c.state);
        formG1.getTextField(`zip${suffix}`).setText(c.zip_code);
        formG1.getTextField(`contract${suffix}`).setText(c.description_of_lease);
        });


    
        // G1 page numbering
        formG1.getTextField('page_total').setText(totalGPages.toString());

        // Hide unchecked, flatten, merge
        const wE = pageG1.getWidth(), hE = pageG1.getHeight();
        formG1.getFields().forEach(field => {
          if (field instanceof PDFCheckBox && !field.isChecked()) {
            field.acroField.getWidgets().forEach(widget => {
              const dict = widget.acroAnnotation?.dict || widget.dict;
              // move widget off-page by setting its Rect to a small area beyond the page bounds
              dict.set(
                PDFName.of('Rect'),
                g1Doc.context.obj([
                  wE + 5,   // lower-left x just outside page
                  hE + 5,  // lower-left y just outside page
                  wE + 15,  // upper-right x a bit further out
                  hE + 15  // upper-right y a bit further out
                ])
              );
            });
          }
        });

        formG1.getTextField('page_total').setText(totalGPages.toString());
        sharedPageCount++;
        formG1.flatten();
        const [g1Page] = await merged.copyPages(g1Doc, [0]);
        merged.addPage(g1Page);
    }

    // 3. Schedule G – Continuation pages (priority creditors beyond first five,  8 per page)
    const contG = data.debtsG.slice(5);
    for (let i = 0; i < contG.length; i += 8) {
      const chunk = contG.slice(i, i + 8);
      const g2Bytes = await fetch(G_PAGE2).then(r => r.arrayBuffer());
      const g2Doc = await PDFDocument.load(g2Bytes);
      const pageG2 = g2Doc.getPage(0);
      const formG2 = g2Doc.getForm();

      // Fill up to eight creditors per page

      chunk.forEach((c, idx) => {
        const n = idx + 1; // 1,2,3,...,8
        const suffix = `_${n}`;
        // Creditor number: 3.<global index>
        const creditorIndex = i + idx + 6;
        formG2.getTextField(`lease${suffix}`).setText(`${creditorIndex}`);

        formG2.getTextField(`account_name${suffix}`).setText(c.account_name);
        formG2.getTextField(`street${suffix}`).setText(`${c.street1} ${c.street2}`);
        formG2.getTextField(`city${suffix}`).setText(c.city);
        formG2.getTextField(`state${suffix}`).setText(c.state);
        formG2.getTextField(`zip${suffix}`).setText(c.zip_code);
        formG2.getTextField(`contract${suffix}`).setText(c.description_of_lease);

      });
      if (primaryDebtor) {
        formG2.getTextField('Debtor 1').setText(
          [primaryDebtor.first_name, primaryDebtor.second_name, primaryDebtor.last_name]
            .filter(Boolean).join(' ')
        );
      }

      // Hide unchecked and flatten
      const wE2 = pageG2.getWidth(), hE2 = pageG2.getHeight();
      formG2.getFields().forEach(field => {
        if (field instanceof PDFCheckBox && !field.isChecked()) {
          field.acroField.getWidgets().forEach(widget => {
            const dict = widget.acroAnnotation?.dict || widget.dict;
            // move widget off-page by setting its Rect to a small area beyond the page bounds
            dict.set(
              PDFName.of('Rect'),
              g2Doc.context.obj([
                wE2 + 5,   // lower-left x just outside page
                hE2 + 5,  // lower-left y just outside page
                wE2 + 15,  // upper-right x a bit further out
                hE2 + 15  // upper-right y a bit further out
              ])
            );
          });
        }
      });
      formG2.getTextField('page_count').setText(sharedPageCount.toString());
      formG2.getTextField('page_total').setText(totalGPages.toString());
      sharedPageCount++;

      formG2.flatten();
      const [g2Page] = await merged.copyPages(g2Doc,[0]);
      merged.addPage(g2Page);
    }

  } // End of Schedule G

    // Start of Schedule H
  if (selectedSchedules.includes('H') || selectedSchedules.includes('all')){
            // Executory COntract debts array
        sharedPageCount = 1;
        const codebtors = data.allDebts.filter(
          d => d.codebtor != null && d.codebtor !== ""
        );
        // Total E pages (1 main + continuation)
        const totalHPages = 1 + Math.ceil(Math.max(codebtors.length - 3, 0) / 8);
    {

        // Load the PDF template
        const h1Bytes = await fetch(H_PAGE1).then(r => r.arrayBuffer());
        const h1Doc   = await PDFDocument.load(h1Bytes);
        const pageH1  = h1Doc.getPage(0);
        const formH1  = h1Doc.getForm();
        if (data.amended) formH1.getCheckBox('amended').check()
        // Case metadata'
          if (codebtors.length > 0) {
          formH1.getCheckBox('codebtor_yes').check()
        }
        else{
          formH1.getCheckBox('codebtor_no').check()
        }
      if (data.caseInfo.case_number)
        formH1.getTextField('case_number').setText(data.caseInfo.case_number);
        formH1.getTextField('district').setText('Eastern District of Tennessee');
        
        // Debtor names: primary = debtor1, secondary = debtor2 (only if filing)
        if (primaryDebtor) {
          formH1.getTextField('Debtor 1').setText(
            [primaryDebtor.first_name, primaryDebtor.second_name, primaryDebtor.last_name]
              .filter(Boolean).join(' ')
          );
        }
        if (secondaryDebtor) {
          formH1.getTextField('Debtor 2').setText(
            [secondaryDebtor.first_name, secondaryDebtor.second_name, secondaryDebtor.last_name]
              .filter(Boolean).join(' ')
          );
        }

        formH1.getCheckBox('community_no').check()

        // Fill the first three entries
        codebtors.slice(0,3).forEach((c, idx) => {
          const suffix = `_${idx + 1}`;
          let groupArray;
          switch (c.debt_type) {
            case 'secured':
              groupArray = data.debtsD;
              break;
            case 'priority':
              groupArray = data.debtsE;
              break;
            case 'unsecured':
              groupArray = data.debtsF;
              break;
            case 'executory':
              groupArray = data.debtsG;
              break;
            default:
              groupArray = [];
          }
          // Find this creditor’s position within its group
          const localIndex = groupArray.findIndex(d => d.id === c.id) + 1;
          // Schedule prefixes: 4 for unsecured, 2 for all others
          const prefix = c.debt_type === 'unsecured' ? '4' : '2';
          const lineNumber = `${prefix}.${localIndex}`;
          // Creditor info
          formH1.getTextField(`codebtor_name${suffix}`).setText(c.codebtor);
          formH1.getTextField(`street${suffix}`).setText(`${c.codebtor_street1} ${c.codebtor_street2}`);
          formH1.getTextField(`city${suffix}`).setText(c.codebtor_city);
          formH1.getTextField(`state${suffix}`).setText(c.codebtor_state);
          formH1.getTextField(`zip${suffix}`).setText(c.codebtor_zip_code);
          if (c.debt_type === 'secured') {
            formH1.getCheckBox(`schedule_D_check${suffix}`).check();
            formH1.getTextField(`schedule_D_line${suffix}`).setText(lineNumber);
          } else if (c.debt_type === 'executory') {
            formH1.getCheckBox(`schedule_G_check${suffix}`).check();
            formH1.getTextField(`schedule_G_line${suffix}`).setText(lineNumber);
          } else if (c.debt_type === 'priority') {
            formH1.getCheckBox(`schedule_E_check${suffix}`).check();
            formH1.getTextField(`schedule_E_line${suffix}`).setText(lineNumber);
          } else {
            formH1.getCheckBox(`schedule_E_check${suffix}`).check();
            formH1.getTextField(`schedule_E_line${suffix}`).setText(lineNumber);
          }
        });


    
        // H1 page numbering
        formH1.getTextField('page_total').setText(totalHPages.toString());

        // Hide unchecked, flatten, merge
        const wE = pageH1.getWidth(), hE = pageH1.getHeight();
        formH1.getFields().forEach(field => {
          if (field instanceof PDFCheckBox && !field.isChecked()) {
            field.acroField.getWidgets().forEach(widget => {
              const dict = widget.acroAnnotation?.dict || widget.dict;
              // move widget off-page by setting its Rect to a small area beyond the page bounds
              dict.set(
                PDFName.of('Rect'),
                h1Doc.context.obj([
                  wE + 5,   // lower-left x just outside page
                  hE + 5,  // lower-left y just outside page
                  wE + 15,  // upper-right x a bit further out
                  hE + 15  // upper-right y a bit further out
                ])
              );
            });
          }
        });

        formH1.getTextField('page_total').setText(totalHPages.toString());
        sharedPageCount++;
        formH1.flatten();
        const [h1Page] = await merged.copyPages(h1Doc, [0]);
        merged.addPage(h1Page);
    }

    // 3. Schedule H – Continuation pages (priority creditors beyond first five,  8 per page)
    const contH = codebtors.slice(3);
    for (let i = 0; i < contH.length; i += 8) {
      const chunk = contH.slice(i, i + 8);
      const h2Bytes = await fetch(H_PAGE2).then(r => r.arrayBuffer());
      const h2Doc = await PDFDocument.load(h2Bytes);
      const pageH2 = h2Doc.getPage(0);
      const formH2 = h2Doc.getForm();

      // Fill up to eight creditors per page

      chunk.forEach((c, idx) => {
        const n = idx + 1; // 1,2,3,...,8
        const suffix = `_${n}`;
        let groupArray;
        switch (c.debt_type) {
          case 'secured':
            groupArray = data.debtsD;
            break;
          case 'priority':
            groupArray = data.debtsE;
            break;
          case 'unsecured':
            groupArray = data.debtsF;
            break;
          case 'executory':
            groupArray = data.debtsG;
            break;
          default:
            groupArray = [];
        }
        const localIndex = groupArray.findIndex(d => d.id === c.id) + 1;
        // Schedule prefixes: 4 for unsecured, 2 for all others
        const prefix = c.debt_type === 'unsecured' ? '4' : '2';
        const lineNumber = `${prefix}.${localIndex}`;
        // Creditor number: 3.<global index>
        const creditorIndex = i + idx + 4;
        formH2.getTextField(`codebtor${suffix}`).setText(`${creditorIndex}`);

        formH2.getTextField(`codebtor_name${suffix}`).setText(c.codebtor);
        formH2.getTextField(`street${suffix}`).setText(`${c.codebtor_street1} ${c.codebtor_street2}`);
        formH2.getTextField(`city${suffix}`).setText(c.codebtor_city);
        formH2.getTextField(`state${suffix}`).setText(c.scodebtor_tate);
        formH2.getTextField(`zip${suffix}`).setText(c.codebtor_zip_code);
        if (c.debt_type === 'secured') {
          formH2.getCheckBox(`schedule_d_check${suffix}`).check();
          formH2.getTextField(`schedule_d_line${suffix}`).setText(lineNumber);
        } else if (c.debt_type === 'executory') {
          formH2.getCheckBox(`schedule_G_check${suffix}`).check();
          formH2.getTextField(`schedule_G_line${suffix}`).setText(lineNumber);
        } else if (c.debt_type === 'priority') {
          formH2.getCheckBox(`schedule_E_check${suffix}`).check();
          formH2.getTextField(`schedule_E_line${suffix}`).setText(lineNumber);
        } else {
          formH2.getCheckBox(`schedule_E_check${suffix}`).check();
          formH2.getTextField(`schedule_E_line${suffix}`).setText(lineNumber);
        }
      });
      if (primaryDebtor) {
        formH2.getTextField('Debtor 1').setText(
          [primaryDebtor.first_name, primaryDebtor.second_name, primaryDebtor.last_name]
            .filter(Boolean).join(' ')
        );
      }

      // Hide unchecked and flatten
      const wE2 = pageH2.getWidth(), hE2 = pageH2.getHeight();
      formH2.getFields().forEach(field => {
        if (field instanceof PDFCheckBox && !field.isChecked()) {
          field.acroField.getWidgets().forEach(widget => {
            const dict = widget.acroAnnotation?.dict || widget.dict;
            // move widget off-page by setting its Rect to a small area beyond the page bounds
            dict.set(
              PDFName.of('Rect'),
              h2Doc.context.obj([
                wE2 + 5,   // lower-left x just outside page
                hE2 + 5,  // lower-left y just outside page
                wE2 + 15,  // upper-right x a bit further out
                hE2 + 15  // upper-right y a bit further out
              ])
            );
          });
        }
      });
      formH2.getTextField('page_count').setText(sharedPageCount.toString());
      formH2.getTextField('page_total').setText(totalHPages.toString());
      sharedPageCount++;

      formH2.flatten();
      const [h2Page] = await merged.copyPages(h2Doc,[0]);
      merged.addPage(h2Page);
    }

  }
  let decPdfDoc = null
  let amendedPdfDoc = null;
      if (data.amended){
        
          const decUrl = '/forms/dec.pdf'
          const decExistingPdfBytes = await fetch(decUrl).then(res => res.arrayBuffer());
          decPdfDoc = await PDFDocument.load(decExistingPdfBytes);
          const decForm = decPdfDoc.getForm();

          (data.caseInfo.debtors.filter(d => d.is_filing) || []).forEach((debtor, index) => {
              const amendPrefix = debtor.is_primary ? 'Debtor 1' : 'Debtor 2';
              decForm.getTextField(`${amendPrefix}`).setText(`${(debtor.first_name || '')} ${(debtor.second_name || '')} ${(debtor.last_name || '')} ${(debtor.suffix || '')}`);
          });

          (data.caseInfo.debtors.filter(d => d.is_filing) || []).forEach((debtor, index) => {
              const amendPrefix = debtor.is_primary ? 'Debtor1' : 'Debtor2';
              decForm.getTextField(`${amendPrefix}.signature`).setText(`s/ ${(debtor.first_name || '')} ${(debtor.second_name || '')} ${(debtor.last_name || '')} ${(debtor.suffix || '')}`);
              decForm.getTextField(`${amendPrefix}.Executed on`).setText(fmtDate(isoToday()));
          });

          decForm.getCheckBox(`no_preparer`).check();
          decForm.getCheckBox(`amended`).check();
          decForm.getTextField('case_number').setText(data.caseInfo.case_number || '');
          decForm.getTextField('district').setText("Eastern District of Tennessee");
          const page1 = decPdfDoc.getPage(0);
          const width1 = page1.getWidth();
          const height1 = page1.getHeight();
          decForm.getFields().forEach(field => {
              if (field instanceof PDFCheckBox && !field.isChecked()) {
                field.acroField.getWidgets().forEach(widget => {
                  const dict = widget.acroAnnotation?.dict || widget.dict;
                  // move widget off-page by setting its Rect to a small area beyond the page bounds
                  dict.set(
                    PDFName.of('Rect'),
                    decPdfDoc.context.obj([
                      width1 + 5,   // lower-left x just outside page
                      height1 + 5,  // lower-left y just outside page
                      width1 + 15,  // upper-right x a bit further out
                      height1 + 15  // upper-right y a bit further out
                    ])
                  );
                });
              }
            });

          const helvetica = await decPdfDoc.embedFont(StandardFonts.Helvetica);
          decForm.updateFieldAppearances(helvetica);
          decForm.flatten();

          const amendedUrl = '/forms/notice-of-amendment.pdf'
          const amendedExistingPdfBytes = await fetch(amendedUrl).then(res => res.arrayBuffer());
          amendedPdfDoc = await PDFDocument.load(amendedExistingPdfBytes);
          const amendedForm = amendedPdfDoc.getForm();
  
          (data.caseInfo.debtors || []).forEach((debtor, index) => {
              const amendPrefix = debtor.is_primary ? 'debtor1' : 'debtor2';
              amendedForm.getTextField(`${amendPrefix}`).setText(`${(debtor.first_name || '')} ${(debtor.second_name || '')} ${(debtor.last_name || '')} ${(debtor.suffix || '')}`);
          });
          amendedForm.getTextField('case_number').setText(data.caseInfo.case_number || '');
          amendedForm.getTextField('chapter').setText(data.caseInfo.chapter || '');
          amendedForm.getTextField('date').setText(fmtLongDate(isoToday()));
          // dynamically build amended files list excluding SOI, Matrix, and 'all'
          // determine which schedules to list when building amended labels
          const scheduleTypes = selectedSchedules.includes('all')
            ? ['D', 'EF', 'G', 'H']
            : selectedSchedules.filter(type => !['SOI', 'Matrix', 'all'].includes(type));
          const scheduleLabels = scheduleTypes
            .map(type => `Schedule ${type}`)
            .join(', ');
          amendedForm.getTextField('amended_files').setText(`${scheduleLabels}`);
          const servedItems = [
            `Amended ${scheduleLabels}`,
            'Declaration',
            'Notice of Amendment',
          ];
          const caseNoticeLabel = requiresCaseFilingNotice
            ? caseFilingNoticeServiceLabel(data)
            : '';
          if (caseNoticeLabel) servedItems.push(caseNoticeLabel);
          const itemsServedField = amendedForm.getTextField('items_served');
          itemsServedField.enableMultiline();
          itemsServedField.setFontSize(8);
          itemsServedField.setText(servedItems.join(', '));
          amendedForm.getTextField('reason_amended').setText(data.amendedReason);
          const serviceDescriptionField = amendedForm.getTextField('creditors_served_description');
          serviceDescriptionField.enableMultiline();
          serviceDescriptionField.setFontSize(8);
          serviceDescriptionField.setText(amendmentServiceDescription(data));

          amendedForm.updateFieldAppearances(helvetica);
          amendedForm.flatten();
          
      }
          if (data.amended && amendedPdfDoc) {
        // then amended form
        const decPages = await merged.copyPages(decPdfDoc, decPdfDoc.getPageIndices());
        decPages.forEach(p => merged.addPage(p));
        const amendedPages = await merged.copyPages(amendedPdfDoc, amendedPdfDoc.getPageIndices());
        amendedPages.forEach(p => merged.addPage(p));
        // then creditor list
        await appendAmendmentServiceList(merged, data, generateCreditorsListPdf);
        const envelopes = await generateEnvelopesPdf(amendmentServiceRecipients(data));
        if (requiresCaseFilingNotice) {
          await generateCaseFilingNoticeServiceCopy(data);
        }
      }
    // Save & trigger download
    let DEFBlob;
    if (selectedSchedules.some(s => ['D','EF','G','H','all'].includes(s))) {
      const pdfBytes = await merged.save({
        useObjectStreams: false,
        compress: false,
      });
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const formData2 = new FormData();
      formData2.append('file', blob, 'draft.pdf');
      const primaryName = [primaryDebtor.first_name, primaryDebtor.second_name, primaryDebtor.last_name]
        .filter(Boolean).join(' ');
      // The synchronous Ghostscript endpoint can exceed the web-server timeout
      // when a large court matrix is attached. Matrix packets are already flat,
      // so keep the locally generated PDF and avoid that unnecessary round trip.
      if (mailingMatrixService(data)) {
        DEFBlob = blob;
      } else {
        const response = await fetch(`${API_URL2}/optimize-pdf/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData2,
          credentials: 'include',
        });

        if (response.ok) {
          DEFBlob = await response.blob();
        } else {
          console.error("Optimization failed");
          DEFBlob = blob;  // fallback to original merged blob
        }
      }

      // Optional: trigger download here
      //const link = document.createElement('a');
      //link.href = URL.createObjectURL(DEFBlob);
      //link.download = response.ok ? `${primaryName}_ScheduleDEF.pdf` : `${primaryName}_ScheduleDEF-failed.pdf`;
      //link.click();

      
    }
    let matrixBlob
    // If Matrix or all schedules are selected, build and download a standalone Matrix+Creditors PDF
    if (selectedSchedules.includes('Matrix') || selectedSchedules.includes('all')) {
      const primaryName = [primaryDebtor.first_name, primaryDebtor.second_name, primaryDebtor.last_name]
        .filter(Boolean).join(' ');
      const mergedMatrix = await PDFDocument.create();

      // ======== MATRIX FORM PDF FILLING AND APPEND SECTION ========
      const matrixBytes = await fetch(MATRIX_FORM).then(r => r.arrayBuffer());
      const matrixDoc = await PDFDocument.load(matrixBytes);
      const matrixForm = matrixDoc.getForm();
      if (primaryDebtor) {
        matrixForm.getTextField('debtor1').setText(
          [primaryDebtor.first_name, primaryDebtor.second_name, primaryDebtor.last_name].filter(Boolean).join(' ')
        );
        matrixForm.getTextField('debtor1_sig').setText(
          's/ ' + [primaryDebtor.first_name, primaryDebtor.second_name, primaryDebtor.last_name].filter(Boolean).join(' ')
        );
        if (data.amended)
        matrixForm.getTextField('debtor1_date').setText(fmtDate(isoToday()));
      else matrixForm.getTextField('debtor1_date').setText(fmtDate(data.caseInfo.execution_date));
      }
      if (secondaryDebtor) {
        matrixForm.getTextField('debtor2').setText(
          [secondaryDebtor.first_name, secondaryDebtor.second_name, secondaryDebtor.last_name].filter(Boolean).join(' ')
        );
        matrixForm.getTextField('debtor2_sig').setText(
          's/ ' + [secondaryDebtor.first_name, secondaryDebtor.second_name, secondaryDebtor.last_name].filter(Boolean).join(' ')
        );
        if (data.amended)
        matrixForm.getTextField('debtor2_date').setText(fmtDate(isoToday()));
      else matrixForm.getTextField('debtor2_date').setText(fmtDate(data.caseInfo.execution_date));
      }
      if (data.caseInfo.case_number) matrixForm.getTextField('case_number').setText(data.caseInfo.case_number);
      if (data.caseInfo.chapter) matrixForm.getTextField('chapter').setText(data.caseInfo.chapter);
      if (data.amended) matrixForm.getTextField('attorney_date').setText(fmtDate(isoToday()));
        else matrixForm.getTextField('attorney_date').setText(fmtDate(data.caseInfo.attorney_date || data.caseInfo.execution_date));
      if (data.amended) matrixForm.getTextField('amended').setText('AMENDED');
      const helveticaFont = await matrixDoc.embedFont(StandardFonts.Helvetica);
      matrixForm.updateFieldAppearances(helveticaFont);
      matrixForm.flatten();

      const allPages = matrixDoc.getPageIndices();
      let goodPages = allPages;
      if (allPages.length > 2) {
        goodPages = allPages.slice(1, -1);
      } else if (allPages.length > 1) {
        goodPages = allPages.slice(1);
      }
      const matrixPages = await mergedMatrix.copyPages(matrixDoc, goodPages);
      matrixPages.forEach(p => mergedMatrix.addPage(p));
      // Append creditor list
      const listDoc = await generateCreditorsListPdf2([
        ...data.debtsD,
        ...data.debtsE,
        ...data.debtsF,
        ...data.debtsG,
      ]);
      const listPages = await mergedMatrix.copyPages(listDoc, listDoc.getPageIndices());
      listPages.forEach(p => mergedMatrix.addPage(p));

      // Save and trigger download
      const finalBytes = await mergedMatrix.save({ useObjectStreams: false, compress: false });
      const finalBlob = new Blob([finalBytes], { type: 'application/pdf' });
      matrixBlob = finalBlob
      //const link = document.createElement('a');
      //link.href = URL.createObjectURL(finalBlob);
      //link.download = `${primaryName}_Matrix_and_Creditors.pdf`;
      //link.click();

      // Also export creditors list with addresses as a text file
      if (creditors && creditors.length) {
        console.log("Exporting creditors:", creditors);
        const textBlob = buildCreditorsTextBlob(creditors);
        const textLink = document.createElement('a');
        textLink.href = URL.createObjectURL(textBlob);
        textLink.download = 'creditors.txt';
        document.body.appendChild(textLink);
        textLink.click();
        document.body.removeChild(textLink);
      }
      
    }
    return [DEFBlob, matrixBlob];
  }

export async function generateChapter7ServicePacket(data) {
  return generateSchedulesPdfLocal(data, false, ['SOI'], [], {
    servicePacketOnly: true,
  });
}

function ScheduleDEF() {
  const [data, setData] = useState({
    debtsD: [], // array of secured creditors
    debtsE: [], // array of priority creditors
    debtsF: [], // array of nonpriority unsecured creditors
    debtsG: [],
    notifyEF: [], // array of others to notify
    allDebts: [],
    caseInfo: {}, // case metadata: number, name, etc.
    amended: false,
    amendedReason: '',
    amendedCreditors: [],
    mailingMatrixService: null,
    amendedScheduleService: {
      filing_debtors: [],
      notice_candidates: [],
      other_docket_candidates: [],
      load_error: '',
    },
    caseFilingNotice: null,
  });
  const [isGenerating, setIsGenerating] = useState(false);
  // Controls which schedules to generate: 'D', 'EF', or 'all'
  const [selectedSchedules, setSelectedSchedules] = useState(['all']);
  // Amended filing controls
  const [amended, setAmended] = useState(false);
  const [showAmendedModal, setShowAmendedModal] = useState(false);
  const [amendedReason, setAmendedReason] = useState('');
  const [amendStep, setAmendStep] = useState(1);
  const [amendedCreditors, setAmendedCreditors] = useState([]);
  const [mailingMatrixService, setMailingMatrixService] = useState(null);
  const [caseFilingNotice, setCaseFilingNotice] = useState(null);
  const [amendmentError, setAmendmentError] = useState('');
  const [creditors, setCreditors] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const { caseId } = useParams();

  const credRef = useRef(creditors)
  useEffect(() => { credRef.current = creditors; }, [creditors]);
  const dataRef = useRef(data);
  useEffect(() => { dataRef.current = data; }, [data]);
  
  const fetchLatestRef = useRef(async () => {});
  useEffect(() => {
  async function fetchData() {
    try {
      const token = localStorage.getItem("accessToken");
      // Fetch case details and creditors in parallel
      const [caseRes, credRes, serviceRes, linkableAssetsRes] = await Promise.all([
        fetch(`${API_URL}/case/${caseId}/creditors/case-details/`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/case/${caseId}/creditors/`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/case/${caseId}/amended-schedule-service/`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/case/${caseId}/creditors/linkable-assets/`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (!caseRes.ok || !credRes.ok) {
        console.error("Failed to fetch case or creditors");
        return;
      }

      const caseJson = await caseRes.json();
      const credJson = await credRes.json();
      const linkableAssetsJson = linkableAssetsRes.ok
        ? await linkableAssetsRes.json()
        : { assets: [] };
      const caseInfo = {
        ...caseJson,
        linkable_assets: Array.isArray(linkableAssetsJson.assets)
          ? linkableAssetsJson.assets
          : [],
      };
      let serviceJson;
      if (serviceRes.ok) {
        serviceJson = await serviceRes.json();
      } else {
        const serviceError = await serviceRes.json().catch(() => ({}));
        serviceJson = {
          filing_debtors: [],
          notice_candidates: [],
          other_docket_candidates: [],
          load_error: serviceError.detail
            || `The notice docket list could not be loaded (${serviceRes.status}).`,
        };
      }

      // Build a simple address list for UI display
      const creditorAddresses = credJson.map(c => ({
        name: c.account_name || c.name,
        street1: c.street1 || '',
        street2: c.street2 || '',
        city: c.city || '',
        state: c.state || '',
        zip_code: c.zip_code || '',
        codebtor: c.codebtor || '',
        codebtor_street1: c.codebtor_street1 || '',
        codebtor_street2: c.codebtor_street2 || '',
        codebtor_state: c.codebtor_state || '',
        codebtor_zip_code: c.codebtor_zip_code || '',
        codebtor_city: c.codebtor_city || '',
      }));
      setCreditors(creditorAddresses);
      const preferredNotice = dataRef.current.caseFilingNotice
        || serviceJson.notice_candidates?.find(candidate => candidate.has_pdf)
        || serviceJson.notice_candidates?.[0]
        || null;
      const nextData = {
        ...dataRef.current,
        caseInfo,
        debtsD: credJson.filter(d => d.debt_type === "secured"),
        debtsE: credJson.filter(d => d.debt_type === "priority"),
        debtsF: credJson.filter(d => d.debt_type === "unsecured"),
        debtsG: credJson.filter(d => d.debt_type === "executory"),
        notifyEF: credJson.filter(d => d.additional_notified),
        allDebts: credJson,
        amendedScheduleService: serviceJson,
        caseFilingNotice: preferredNotice,
      };
      dataRef.current = nextData;
      setData(nextData);
      setCaseFilingNotice(preferredNotice);
      return { data: nextData, creditors: creditorAddresses };
    } catch (err) {
      console.error("Error fetching schedule data:", err);
    } finally {
      setLoading(false);
    }
  }

  // store for on-demand refreshes before generating
  fetchLatestRef.current = fetchData;
  // initial fetch
  fetchData();
}, [caseId, API_URL]);
const handleGenerate = async () => {
  try {
    setIsGenerating(true);
    setAmendmentError('');
    const latest = await fetchLatestRef.current();
    const generationData = latest?.data || dataRef.current;
    const generationCreditors = latest?.creditors || credRef.current;
    const [schedulePdf, matrixPdf] = await generateSchedulesPdfLocal(
      generationData,
      generationData.amended,
      selectedSchedules,
      generationCreditors,
    );
    if (schedulePdf) {
      const scheduleUrl = URL.createObjectURL(schedulePdf);
      const link = document.createElement('a');
      link.href = scheduleUrl;
      link.download = 'Schedule DEF.pdf';
      link.click();
      setTimeout(() => URL.revokeObjectURL(scheduleUrl), 1000);
    }
    if (matrixPdf) {
      const matrixUrl = URL.createObjectURL(matrixPdf);
      const link = document.createElement('a');
      link.href = matrixUrl;
      link.download = 'Creditor Matrix.pdf';
      link.click();
      setTimeout(() => URL.revokeObjectURL(matrixUrl), 1000);
    }
  } catch (e) {
    console.error('Generate failed:', e);
    setAmendmentError(e.message || 'The schedules could not be generated.');
  } finally {
    setIsGenerating(false);
  }
};
  const amendedDefSelected = selectedSchedules.includes('all')
    || selectedSchedules.includes('D')
    || selectedSchedules.includes('EF');
  const recommendedNoticeCandidates = data.amendedScheduleService?.notice_candidates || [];
  const otherNoticeCandidates = data.amendedScheduleService?.other_docket_candidates || [];
  const noticeChoices = [
    ...recommendedNoticeCandidates,
    ...otherNoticeCandidates.filter(candidate => (
      !recommendedNoticeCandidates.some(recommended => (
        String(recommended.docket_id) === String(candidate.docket_id)
      ))
    )),
  ];
  if (loading) {
    return <div>Loading...</div>;
  }



  return (
    <div>
      {showAmendedModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded bg-white p-6 shadow">
            {amendStep === 1 ? (
              <>
                <h3 className="text-lg font-semibold mb-2">Reason for Amendment</h3>
                <textarea
                  value={amendedReason}
                  onChange={e => setAmendedReason(e.target.value)}
                  className="w-full border rounded p-2"
                  rows={4}
                />
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={() => setAmendStep(2)}
                    className="px-4 py-2 bg-blue-600 text-white rounded"
                  >
                    Next
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold mb-2">Select Creditors to Serve</h3>
                {amendedDefSelected && (
                  <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3">
                    <label className="block text-sm font-semibold text-amber-950">
                      Notice of Bankruptcy Case to include with mailed service
                    </label>
                    {data.amendedScheduleService?.load_error ? (
                      <div className="mt-2 text-sm font-medium text-red-700">
                        <p>{data.amendedScheduleService.load_error}</p>
                        <button
                          type="button"
                          onClick={() => fetchLatestRef.current()}
                          className="mt-2 rounded border border-red-300 bg-white px-3 py-1 text-red-800"
                        >
                          Retry loading notice dockets
                        </button>
                      </div>
                    ) : noticeChoices.length > 0 ? (
                      <>
                        <select
                          value={caseFilingNotice?.docket_id || ''}
                          onChange={event => {
                            const selected = noticeChoices
                              .find(item => String(item.docket_id) === event.target.value);
                            setCaseFilingNotice(selected || null);
                            setAmendmentError('');
                          }}
                          className="mt-2 block w-full rounded border border-amber-300 bg-white px-2 py-2 text-sm"
                        >
                          <option value="">Select the notice docket</option>
                          {recommendedNoticeCandidates.length > 0 && (
                            <optgroup label="Recommended notice dockets">
                              {recommendedNoticeCandidates.map(candidate => (
                                <option key={candidate.docket_id} value={candidate.docket_id}>
                                  Docket {candidate.docket_number} - {(candidate.docket_text || '').slice(0, 110)}
                                  {candidate.has_pdf ? '' : ' (PDF not saved)'}
                                </option>
                              ))}
                            </optgroup>
                          )}
                          {otherNoticeCandidates.length > 0 && (
                            <optgroup label="Other saved docket PDFs">
                              {otherNoticeCandidates.map(candidate => (
                                <option key={candidate.docket_id} value={candidate.docket_id}>
                                  Docket {candidate.docket_number} - {(candidate.docket_text || '').slice(0, 110)}
                                </option>
                              ))}
                            </optgroup>
                          )}
                        </select>
                        {recommendedNoticeCandidates.length === 0 && (
                          <p className="mt-2 text-sm font-medium text-amber-900">
                            The notice wording was not recognized automatically. Select the correct Notice of Bankruptcy Case from the saved docket PDFs below.
                          </p>
                        )}
                        {caseFilingNotice && !caseFilingNotice.has_pdf && (
                          <p className="mt-2 text-sm font-medium text-red-700">
                            This docket has no saved PDF. Open Fetch Dockets and retrieve it before generating service copies.
                          </p>
                        )}
                        <p className="mt-2 text-xs text-amber-900">
                          A separate service-only PDF will contain the full SSN for each filing debtor and the selected court notice. The filed amendment will cite the docket but will not expose the SSNs.
                        </p>
                      </>
                    ) : (
                      <p className="mt-2 text-sm font-medium text-red-700">
                        No saved docket PDFs were found. Open Fetch Dockets, retrieve the Notice of Bankruptcy Case PDF, and then return here.
                      </p>
                    )}
                  </div>
                )}
                <MailingMatrixServiceSelector
                  caseId={caseId}
                  value={mailingMatrixService}
                  onChange={setMailingMatrixService}
                />
                <div className="mb-2 text-sm font-semibold text-gray-800">
                  {mailingMatrixService?.enabled
                    ? 'Additional creditors or service-method overrides'
                    : 'Individual creditors to serve'}
                </div>
                <button
                  onClick={() =>
                    setAmendedCreditors(creditors.map(c => ({ ...c, method: 'USM' })))
                  }
                  disabled={mailingMatrixService?.enabled}
                  className="text-sm text-blue-600 mb-2"
                >
                  Select All as USM
                </button>
                <button
                  onClick={() => setAmendedCreditors([])}
                  className="ml-4 text-sm text-red-600 mb-2"
                >
                  Remove All
                </button>
                <div className="max-h-64 overflow-y-auto mb-4 border">
                  {creditors.map((c, idx) => {
                    const selected = amendedCreditors.find(ac => ac.name === c.name);
                    return (
                      <div key={idx} className="flex items-center justify-between p-2 border-b">
                        <div className="flex-1">
                          <div className="font-medium">{c.name}</div>
                          <div className="text-sm text-gray-500">
                            {c.street1}{c.street2?.trim() ? `, ${c.street2}` : ''}, {c.city}, {c.state} {c.zip_code}
                          </div>
                        </div>
                        <select
                          value={selected?.method || ''}
                          onChange={e => {
                            const method = e.target.value;
                            setAmendedCreditors(prev => {
                              const exists = prev.find(ac => ac.name === c.name);
                              if (!method) {
                                return prev.filter(ac => ac.name !== c.name);
                              }
                              if (exists) {
                                return prev.map(ac =>
                                  ac.name === c.name ? { ...ac, method } : ac
                                );
                              }
                              return [...prev, { ...c, method }];
                            });
                          }}
                          className="border rounded px-2 py-1"
                        >
                          <option value="">None</option>
                          <option value="USM">USM</option>
                          <option value="Certified">Certified</option>
                        </select>
                      </div>
                    );
                  })}
                </div>
                {amendmentError && (
                  <p className="mb-3 rounded border border-red-200 bg-red-50 p-2 text-sm font-medium text-red-700">
                    {amendmentError}
                  </p>
                )}
                <div className="flex justify-between">
                  <button
                    onClick={() => setAmendStep(1)}
                    className="px-4 py-2 bg-gray-200 rounded"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => {
                      if (amendedDefSelected) {
                        if (!caseFilingNotice) {
                          setAmendmentError('Select the Notice of Bankruptcy Case docket.');
                          return;
                        }
                        if (!caseFilingNotice.has_pdf) {
                          setAmendmentError('The selected notice must have a saved PDF before the service copy can be generated.');
                          return;
                        }
                        const missingSsn = (data.amendedScheduleService?.filing_debtors || [])
                          .filter(debtor => !String(debtor.ssn || '').trim());
                        if (missingSsn.length > 0) {
                          setAmendmentError(`Save the full Social Security number for ${missingSsn.map(item => item.name).join(', ')} before continuing.`);
                          return;
                        }
                      }
                      setShowAmendedModal(false);
                      setAmendmentError('');
                      const nextData = {
                        ...data,
                        amendedReason,
                        amendedCreditors,
                        mailingMatrixService,
                        caseFilingNotice,
                      };
                      dataRef.current = nextData;
                      setData(nextData);
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded"
                  >
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <h1 className="text-2xl font-semibold">Schedules D, E & F</h1>
      {/* Schedule selection & Amended toggle */}
        <div className="flex flex-wrap items-center space-x-4 mb-4">
          {['D','EF','G','SOI','H','Matrix','all'].map(type => {
            const isAll = type === 'all';
            const isSel = selectedSchedules.includes(type);
            return (
              <button
                key={type}
                type="button"
                onClick={() => {
                  setSelectedSchedules(prev => {
                    // clicking “All” resets to just ['all']
                    if (isAll) return ['all'];
                    // otherwise remove 'all'
                    const withoutAll = prev.filter(s => s !== 'all');
                    // toggle this type
                    if (withoutAll.includes(type)) {
                      return withoutAll.filter(s => s !== type);
                    } else {
                      return [...withoutAll, type];
                    }
                  });
                }}
                className={`px-4 py-2 rounded ${
                  isSel ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'
                }`}
              >
                {isAll ? 'All' : `Schedule ${type}`}
              </button>
            );
          })}
 </div>
      <div>
            <button
  onClick={(handleGenerate)}
  disabled={loading || isGenerating}
  className={`mt-4 px-4 py-2 rounded ${(loading || isGenerating) ? 'bg-gray-400' : 'bg-green-600 text-white'}`}
>
  {isGenerating ? 'Generating…' : 'Generate Schedules PDF'}
</button>
      </div>
      {amendmentError && !showAmendedModal && (
        <p className="mt-3 rounded border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700">
          {amendmentError}
        </p>
      )}
              <div className="flex items-center mb-4">
          <input
            type="checkbox"
            checked={data.amended}
            onChange={e => {
              const checked = e.target.checked;
              setData({ ...data, amended: checked });
              if (checked) {
                setAmendmentError('');
                setShowAmendedModal(true);
                setAmendStep(1);
              }
            }}
            className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
          />
          <label className="ml-2 text-sm text-gray-700">Amended Filing</label>
        </div>
    </div>
    
  );
}

export default ScheduleDEF;
